import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { flushSync } from 'react-dom';
import { API } from '../config';
import { APP_VERSION } from '../version';
import { adapterToken } from './serviziAdapter';
import AddressCascade from '../components/AddressCascade';
import { useVicine, VicineControls, VicineFiltri, VicineLista } from '../components/VicineAgente';
import RegistrazionePlayer from '../components/RegistrazionePlayer';
import TabWheel from '../components/TabWheel';
import VodafoneBadge from '../components/VodafoneBadge';
import LucchettoConsistenze from '../components/LucchettoConsistenze';
import TourGuidato from '../components/TourGuidato';
import { statoNotifiche, attiva as attivaPush, disattiva as disattivaPush, setBadge } from '../mobile/push';
import { makeCoperturaPdf, makeCoperturaTimPdf, fmtVel } from '../utils/coperturaPdf';
import CondividiPdfBtn from '../components/CondividiPdfBtn';
import { fonteCopertura } from './fonteCopertura';
import './publicDashboard.css';

const COLS = [
  { k: 'fwa_5g', l: 'FWA 5G', t: 'ok' }, { k: 'fwa_4g', l: 'FWA 4G', t: 'ok' },
  { k: 'fibra_10g', l: 'Fibra 10G', t: 'ok' }, { k: 'fibra_nere', l: 'Fibra nere', t: 'ok' },
  { k: 'fibra_bianche', l: 'Fibra bianche', t: 'ok' }, { k: 'xdsl_30m', l: 'xDSL 30M', t: 'speed' },
  { k: 'xdsl_fast200', l: 'xDSL Fast 200', t: 'speed' }, { k: 'adsl_prof', l: 'ADSL Prof.', t: 'ok' },
  { k: 'ftto', l: 'FTTO', t: 'fascia' }, { k: 'eth_silver', l: 'Eth. Silver', t: 'fascia' },
  { k: 'eth_gold', l: 'Eth. Gold', t: 'fascia' },
  { k: 'fibra_prof_10g', l: 'Fibra Prof. 10G', t: 'ok' },
  { k: 'fibra_prof', l: 'Fibra Prof.', t: 'ok' },
];
const ESITO_COL = { 'TIM': 'var(--err)', 'NON TIM': 'var(--ok)', 'ERRORE': 'var(--err)' };

// Copertura sito TIM (agente): stessa formattazione/colori di CoperturaTimDialog (desktop), su token della PWA.
const fmtVelTim = fmtVel;   // unica formattazione velocita', condivisa col generatore PDF
const esitoColorTim = (e) => {
  const s = (e || '').toUpperCase();
  if (s.includes('NON CENS')) return 'var(--faint)';
  if (s.includes('NON')) return 'var(--err)';
  if (s.includes('COPERT') || s.includes('OK') || s.includes('DISPONIB')) return 'var(--ok)';
  return 'var(--info)';
};

function tokenFromUrl() {
  const m = window.location.pathname.match(/^\/p\/([^/]+)/);
  return m ? decodeURIComponent(m[1]) : '';
}
const Check = () => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5" /></svg>);
function cell(c, v) {
  v = v || {};
  if (c.t === 'ok') return v.esito === 'OK' ? <Check /> : (v.esito === 'GIALLO' ? <span style={{ color: 'var(--warn)' }}>~</span> : <span style={{ color: 'var(--faint)' }}>—</span>);
  if (c.t === 'speed') return (v.esito === 'OK' && (v.up || v.down)) ? <span>{v.up || '?'}/{v.down || '?'}</span> : <span style={{ color: 'var(--faint)' }}>—</span>;
  return <span>{v.fascia || (v.esito === 'OK' ? 'OK' : '—')}</span>;
}

const AGN = {
  num: { title: 'Verifica numero', cls: 'num', phases: { avvio: 'Avvio…', registrazione: 'Chiamata al 456…', trascrizione: 'Analisi risposta…', attesa: 'Attesa…', analisi: 'Analisi esito…', standby: 'In standby…', vodafone: 'Verifica Vodafone…' } },
  cov: { title: 'Copertura SSMATE', cls: 'cov' },
  luc: { title: 'Lucchetto SSMATE', cls: 'luc' },
  dom: { title: 'Verifica dominio', cls: 'dom' },
  ind: { title: 'Indaga numero', cls: 'ind' },
  covtim: { title: 'Copertura sito TIM', cls: 'cov' },
};

// Strumento Dominio: etichette e stato dei 3 passi (456 / Copertura / Lucchetto) nel dettaglio esito.
const DOM_STEP_LABEL = { n456: '456', copertura: 'Copertura', lucchetto: 'Lucchetto' };
function statoStepDom(s) {
  if (!s) return <span style={{ opacity: 0.5 }}>—</span>;
  if (s.stato === 'in_corso') return <span style={{ opacity: 0.7 }}>in corso…</span>;
  if (s.stato === 'attesa') return <span style={{ opacity: 0.6 }}>in attesa…</span>;
  if (s.stato === 'saltato') return <span style={{ opacity: 0.6 }}>saltato · {s.motivo}</span>;
  if (s.stato === 'errore') return <span style={{ color: 'var(--err)' }}>errore · {s.motivo}</span>;
  // Ripiego: SalesMate non disponibile, la copertura l'ha data il sito TIM (18/09/2026).
  // Va detto, perche' e' un'altra fonte e non ha la matrice per servizio.
  if (s.fonte) {
    return (
      <span style={{ fontWeight: 700 }} title={s.motivo || ''}>
        {s.esito || 'fatto'}
        <span style={{ fontWeight: 500, opacity: 0.6, marginLeft: 6 }}>via {s.fonte}</span>
      </span>
    );
  }
  return <span style={{ fontWeight: 700 }}>{s.esito || 'fatto'}</span>;
}

// Le 11 colonne raggruppate come le 3 tab del portale SSMATE.
const TABS = [
  { id: 'RIEPILOGO' },
  { id: 'FWA', keys: ['fwa_5g', 'fwa_4g'] },
  { id: 'PACCHETTI', keys: ['fibra_10g', 'fibra_nere', 'fibra_bianche'] },
  { id: 'PROFILI', keys: ['fibra_prof_10g', 'fibra_prof', 'xdsl_30m', 'xdsl_fast200', 'adsl_prof', 'ftto', 'eth_silver', 'eth_gold'] },
];
// Raggruppamento del RIEPILOGO: FTTO/Ethernity in una categoria dedicata (fibra dedicata business).
const RIEP_GRUPPI = [
  { id: 'FWA', keys: ['fwa_5g', 'fwa_4g'] },
  { id: 'PACCHETTI', keys: ['fibra_10g', 'fibra_nere', 'fibra_bianche'] },
  { id: 'PROFILI', keys: ['fibra_prof_10g', 'fibra_prof', 'xdsl_30m', 'xdsl_fast200', 'adsl_prof'] },
  { id: 'DEDICATA', keys: ['ftto', 'eth_silver', 'eth_gold'] },
];
// Etichette usate nel riassunto backend (riassumi_copertura) — per ricostruire le colonne OK dalle vecchie verifiche.
const ESITO_LABEL = { fwa_5g: 'FWA 5G', fwa_4g: 'FWA 4G', fibra_10g: 'Fibra 10G', fibra_nere: 'Fibra nere', fibra_bianche: 'Fibra bianche', xdsl_30m: 'xDSL 30M', xdsl_fast200: 'xDSL Fast 200', adsl_prof: 'ADSL', ftto: 'FTTO', eth_silver: 'Eth. Silver', eth_gold: 'Eth. Gold', fibra_prof_10g: 'Fibra Prof. 10G', fibra_prof: 'Fibra Prof.' };
function colonneDaEsito(esito) {
  const s = (esito || '').toUpperCase();
  const col = {};
  Object.keys(ESITO_LABEL).forEach(k => { col[k] = { esito: s.includes(ESITO_LABEL[k].toUpperCase()) ? 'OK' : null }; });
  return col;
}
// Etichette del PDF: le stesse mostrate a schermo (e le stesse del PDF del link desktop).
const COL_LABEL = Object.fromEntries(COLS.map(c => [c.k, c.l]));
// Valore della cella nel PDF: velocita' per xDSL, fascia per FTTO/Ethernity, altrimenti "Disponibile".
function valorePdfCov(k, v) {
  v = v || {};
  const c = COLS.find(x => x.k === k) || {};
  if (c.t === 'speed') return (v.up || v.down) ? `${v.up || '?'}/${v.down || '?'} Mbps` : 'Disponibile';
  if (c.t === 'fascia') return v.fascia ? String(v.fascia) : 'Disponibile';
  return 'Disponibile';
}
// Ordine di preferenza commerciale: dalla tecnologia migliore alla peggiore. Mostro solo la prima disponibile.
const PRIORITA_TECH = ['fibra_10g', 'fibra_prof_10g', 'ftto', 'eth_gold', 'eth_silver', 'fibra_prof', 'fibra_nere', 'fibra_bianche', 'fwa_5g', 'fwa_4g', 'xdsl_fast200', 'xdsl_30m', 'adsl_prof'];
function miglioreTecnologia(d) {
  const col = (d.colonne && Object.keys(d.colonne).length) ? d.colonne : colonneDaEsito(d.esito);
  for (const k of PRIORITA_TECH) { if ((col[k] || {}).esito === 'OK') return ESITO_LABEL[k]; }
  return 'Nessuna copertura';
}
const EyeIcon = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flex: '0 0 auto' }}>
    <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z" /><circle cx="12" cy="12" r="3" />
  </svg>
);
const WarnIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flex: '0 0 auto' }}>
    <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);
// Stato di una verifica copertura: 'errore' (verifica non riuscita) / 'nontrovato' / 'ok' (esito valido, anche se senza copertura).
function covStato(d) {
  if (d?.errore) return 'errore';
  const e = (d?.esito || '').trim().toLowerCase();
  if (e === 'errore') return 'errore';
  if (d?.trovato === false || e === 'non trovato') return 'nontrovato';
  return 'ok';
}
// Toast di verifica non riuscita con countdown: riavvia la verifica allo scadere, oppure Annulla.
function RetryToast({ titolo, secondi = 7, onRetry, onCancel }) {
  const [n, setN] = useState(secondi);
  const nRef = useRef(secondi);
  const retryRef = useRef(onRetry);
  retryRef.current = onRetry;
  useEffect(() => {
    const t = setInterval(() => {
      nRef.current -= 1;
      setN(nRef.current);
      if (nRef.current <= 0) { clearInterval(t); retryRef.current(); }
    }, 1000);
    return () => clearInterval(t);
  }, []);
  return (
    <>
      <div className="ti" style={{ color: 'var(--warn)' }}><WarnIcon /></div>
      <div style={{ flex: 1 }}><h5>{titolo}</h5><p>Riprovo automaticamente tra {n}s…</p></div>
      <button onClick={(e) => { e.stopPropagation(); onCancel(); }}
        style={{ border: '1px solid var(--line-2)', background: 'transparent', color: 'var(--muted)', borderRadius: 8, padding: '5px 10px', fontSize: 12, cursor: 'pointer', fontWeight: 700, whiteSpace: 'nowrap' }}>Annulla</button>
    </>
  );
}

// Set icone unico stile SF Symbols (tratto 2.1 arrotondato, geometrie semplici, 24x24):
// telefono (Numero), antenna con onde (Copertura), lucchetto con foro (Lucchetto).
// Usate sia nel segmented control mobile sia nella cronologia e nelle card desktop.
const IcoNum = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21.5 16.9v2.6a2 2 0 0 1-2.2 2 19.3 19.3 0 0 1-8.4-3 19 19 0 0 1-5.9-5.9 19.3 19.3 0 0 1-3-8.4 2 2 0 0 1 2-2.2h2.6a2 2 0 0 1 2 1.7c.13.95.36 1.88.7 2.77a2 2 0 0 1-.45 2.1L7.6 9.8a15.3 15.3 0 0 0 6.1 6.1l1.23-1.23a2 2 0 0 1 2.1-.45c.9.34 1.83.57 2.78.7a2 2 0 0 1 1.7 2z" />
  </svg>
);
const IcoCov = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="8.2" r="1.9" fill="currentColor" stroke="none" />
    <path d="M12 10.8V21" />
    <path d="M8.7 11.5a4.7 4.7 0 0 1 0-6.6" /><path d="M15.3 4.9a4.7 4.7 0 0 1 0 6.6" />
    <path d="M6 14.5a9 9 0 0 1 0-12.6" /><path d="M18 1.9a9 9 0 0 1 0 12.6" />
  </svg>
);
const IcoLuc = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round">
    <rect x="5" y="10.5" width="14" height="10" rx="3.2" />
    <path d="M8.2 10.5V7.3a3.8 3.8 0 0 1 7.6 0v3.2" />
    <circle cx="12" cy="15.5" r="1.4" fill="currentColor" stroke="none" />
  </svg>
);
// Vicine: pin mappa (attivita' vicine per posizione).
const IcoVic = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 21s-6.5-5.4-6.5-10.2A6.5 6.5 0 0 1 12 4.3a6.5 6.5 0 0 1 6.5 6.5C18.5 15.6 12 21 12 21z" />
    <circle cx="12" cy="10.6" r="2.3" />
  </svg>
);
// Dominio: globo/internet (meridiani + paralleli) — stesso stile delle altre icone.
const IcoDom = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="9.3" />
    <path d="M2.7 12h18.6" />
    <path d="M12 2.7c2.55 2.5 3.98 5.85 3.98 9.3S14.55 18.8 12 21.3C9.45 18.8 8.02 15.45 8.02 12S9.45 5.2 12 2.7z" />
  </svg>
);
// Indaga: lente d'ingrandimento (reverse lookup di un numero) — stesso stile.
const IcoInd = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="10.5" cy="10.5" r="6.7" />
    <path d="m20.5 20.5-4.7-4.7" />
  </svg>
);

export default function PublicRicercaPage({ adapter: adapterProp }) {
  const token = tokenFromUrl();
  const adapter = React.useMemo(() => adapterProp || adapterToken(token), [adapterProp, token]);
  const [caps, setCaps] = useState(null);      // { nome, vodafone, copertura, usi_oggi, tetto } | 'invalid'
  const [light, setLight] = useState(false);
  // Cambio tema "a liquido": la View Transitions API scopre la pagina GIÀ col tema nuovo
  // (testi inclusi) mentre un'onda multi-sinusoidale sale ondeggiando. Onda generata a runtime
  // ad alta risoluzione (liscia, un filo diversa ogni volta). Fallback: cambio istantaneo.
  const toggleTheme = () => {
    const next = !light;
    const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce || typeof document === 'undefined' || !document.startViewTransition) { setLight(next); return; }
    // costruisci i keyframes: onda che sale (assestandosi) e ondeggia orizzontalmente
    const N = 40, STEPS = 26, seed = Math.random() * 6.283;   // meno punti (onde grosse) + più frame = più fluido, leggero
    const eOut = (t) => 1 - Math.pow(1 - t, 3);
    let frames = '';
    for (let s = 0; s <= STEPS; s++) {
      const t = s / STEPS, te = eOut(t);
      const base = 114 - 124 * te;              // da 114% (sotto) a -10% (sopra)
      const amp = 9 * (1 - 0.6 * te);           // onde GROSSE, si assestano verso l'orlo
      const ph = seed + t * 5.2;                // fase avanza -> ondeggia (più calmo)
      const pts = [];
      for (let i = 0; i <= N; i++) {
        const x = (i / N) * 100, xr = (x / 100) * 2 * Math.PI;
        // poche onde grandi: 2 sole sinusoidi a bassa frequenza (~2-3 creste)
        const y = base + amp * (Math.sin(xr * 1.35 + ph) + 0.5 * Math.sin(xr * 2.4 + ph * 0.7 + 0.8));
        pts.push(`${x.toFixed(2)}% ${y.toFixed(2)}%`);
      }
      pts.push('100% 100%', '0% 100%');
      frames += `${(t * 100).toFixed(2)}%{clip-path:polygon(${pts.join(',')})}`;
    }
    const style = document.createElement('style');
    style.id = 'theme-vt-kf';
    style.textContent = `@keyframes liquidRevealDyn{${frames}}`;
    document.head.appendChild(style);
    document.documentElement.classList.add('theme-vt');
    const cleanup = () => { document.documentElement.classList.remove('theme-vt'); style.remove(); };
    const vt = document.startViewTransition(() => { flushSync(() => setLight(next)); });
    vt.finished.then(cleanup, cleanup);
  };
  const [stats, setStats] = useState(null);
  const [mieVerifiche, setMieVerifiche] = useState([]);
  const [numero, setNumero] = useState('');
  const [dominioVal, setDominioVal] = useState('');   // input dello strumento Dominio
  const [indagaNum, setIndagaNum] = useState('');     // input dello strumento Indaga (numero → azienda)
  const [cov, setCov] = useState({ provincia: '', comune: '', indirizzo: '', civico: '' });
  const [jobs, setJobs] = useState([]);        // { id, tipo, stato, prog, fase, min, risultato, errore }
  const [focus, setFocus] = useState(null);    // job id in overlay
  const [toasts, setToasts] = useState([]);    // { id, tipo, titolo, sub } (solo avvisi input/stato)
  const [panelOpen, setPanelOpen] = useState(false);   // bollicina verifiche espansa
  const [aperto, setAperto] = useState(null);  // job da mostrare in dettaglio esito
  const [covTab, setCovTab] = useState('RIEPILOGO');
  // Copertura agente: scelta SalesMate/sito TIM dopo Avvia (link pubblico: invariato, solo SalesMate).
  const [covScelta, setCovScelta] = useState(null);   // indirizzo {provincia,comune,indirizzo,civico} in attesa di scelta
  const [covTimRes, setCovTimRes] = useState(null);   // esito copertura sito TIM (finestra risultato, riapribile dalla bollicina)
  const [strum, setStrum] = useState('num');    // segmento strumenti attivo su mobile ('num' | 'cov' | 'luc' | 'dom')
  const [cronEsito, setCronEsito] = useState(null);   // filtro esito (contatore tappato): nontim/tim/coperti/...
  useEffect(() => { setCronEsito(null); }, [strum]);   // cambio strumento -> azzera il filtro esito
  const [swipeHint, setSwipeHint] = useState(false);  // tutorial: mano che scorre sul selettore
  const [menuOpen, setMenuOpen] = useState(false);   // menu hamburger (agente)
  const [notifStato, setNotifStato] = useState('');  // stato notifiche push agente
  const [notifBusy, setNotifBusy] = useState(false);
  const [notifiche, setNotifiche] = useState([]);    // centro notifiche persistente (server)
  const [nonLette, setNonLette] = useState(0);       // non lette -> badge icona
  const [ssmOpen, setSsmOpen] = useState(false);    // dialog credenziali SalesMate
  const [impOpen, setImpOpen] = useState(false);    // dialog Impostazioni (finestra separata)
  // Preferenze PER-AGENTE (posizione barra strumenti + vibrazione): salvate con chiave
  // legata all'utente loggato, cosi' sullo stesso dispositivo ogni agente ha le sue e le
  // ritrova alla riapertura. Default finche' non si conosce chi ha fatto login.
  const [menuPos, setMenuPosState] = useState('su');   // 'su' | 'giu'
  const [vibra, setVibraState] = useState(true);        // feedback aptico rotella (Android)
  const agKey = (base) => `ag:${(caps && (caps.username || caps.nome)) || 'anon'}:${base}`;
  const setMenuPos = (v) => { setMenuPosState(v); try { localStorage.setItem(agKey('menuPos'), v); } catch { /* no-op */ } };
  const setVibra = (v) => { setVibraState(v); try { localStorage.setItem(agKey('vibra'), v ? '1' : '0'); } catch { /* no-op */ } };
  const prefsLoaded = useRef(false);
  useEffect(() => {   // quando l'app conosce l'agente, carica le SUE preferenze salvate
    const u = caps && (caps.username || caps.nome);
    if (!u || prefsLoaded.current) return;
    prefsLoaded.current = true;
    try {
      const mp = localStorage.getItem(`ag:${u}:menuPos`) || localStorage.getItem('agenteMenuPos'); // fallback vecchia chiave
      if (mp === 'giu' || mp === 'su') setMenuPosState(mp);
      const vb = localStorage.getItem(`ag:${u}:vibra`);
      const vbOld = vb == null ? localStorage.getItem('agenteVibra') : null;
      if (vb != null) setVibraState(vb !== '0');
      else if (vbOld != null) setVibraState(vbOld !== '0');
      // strumento attivo: ripristina l'ultimo usato, se ancora abilitato
      const st = localStorage.getItem(`ag:${u}:strum`);
      if (st && caps && caps !== 'invalid') {
        const valid = new Set(['num']);
        if (caps.copertura) valid.add('cov');
        if (caps.lucchetto && adapter.lucchettoStart) valid.add('luc');
        if (caps.dominio && adapter.dominioStart) valid.add('dom');
        if (caps.vicine && adapter.vicineTrova) valid.add('vicine');
        if (caps.indaga && adapter.indagaStart) valid.add('ind');
        if (valid.has(st)) setStrum(st);
      }
    } catch { /* no-op */ }
  }, [caps]);
  // salva lo strumento attivo (solo dopo il ripristino, per non sovrascrivere quello salvato)
  useEffect(() => {
    const u = caps && (caps.username || caps.nome);
    if (!u || !prefsLoaded.current) return;
    try { localStorage.setItem(`ag:${u}:strum`, strum); } catch { /* no-op */ }
  }, [strum, caps]);
  const [ssmPwd, setSsmPwd] = useState(''); const [ssmBusy, setSsmBusy] = useState(false); const [ssmErr, setSsmErr] = useState('');
  const [tourOpen, setTourOpen] = useState(false);           // tour guidato attivo
  const [promptTutorial, setPromptTutorial] = useState(false); // richiesta al primo accesso
  const promptedRef = useRef(false);
  const [cf, setCf] = useState('');             // codice fiscale per lo strumento Lucchetto
  const [covH, setCovH] = useState(0);          // altezza del RIEPILOGO: le altre tab non scendono sotto
  const covContentRef = useRef(null);
  useEffect(() => { setCovH(0); }, [aperto]);   // nuovo dettaglio -> rimisura
  useLayoutEffect(() => {
    if (aperto?.tipo === 'cov' && covTab === 'RIEPILOGO' && covContentRef.current) {
      setCovH(covContentRef.current.offsetHeight);
    }
  }, [aperto, covTab]);
  // Su mobile la dashboard pubblica è un'app-shell a schermo pieno: blocca lo scroll del documento
  // (niente rimbalzo/pull-to-refresh); su desktop resta lo scroll normale. Ripristina allo smontaggio.
  useEffect(() => {
    const mq = window.matchMedia('(max-width:880px)');
    const apply = () => {
      const on = mq.matches;
      document.documentElement.style.overflow = on ? 'hidden' : '';
      document.body.style.overflow = on ? 'hidden' : '';
      document.body.style.overscrollBehavior = on ? 'none' : '';
    };
    apply();
    mq.addEventListener ? mq.addEventListener('change', apply) : mq.addListener(apply);
    return () => {
      mq.removeEventListener ? mq.removeEventListener('change', apply) : mq.removeListener(apply);
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
      document.body.style.overscrollBehavior = '';
    };
  }, []);
  // Manifest DEDICATO: l'app installata deve avere il PERIMETRO della sezione in cui vive.
  // Il manifest predefinito dichiara scope "/m" (il Centralino): installando da /agente,
  // l'app dell'agente nasceva col perimetro sbagliato e /agente restava FUORI. Su iOS un
  // indirizzo fuori perimetro non si apre nella PWA ma in Safari, che ha un archivio a
  // parte: al tap sulla notifica si finiva sulla schermata di accesso, senza sessione.
  // manifest-agente.json esisteva gia' (scope "/agente") ma non era collegato a nulla.
  useEffect(() => {
    const suo = adapter.variant === 'agente' ? '/manifest-agente.json'
      : (adapter.variant === 'link' && token) ? `${API}/pubblico/manifest?token=${encodeURIComponent(token)}`
      : null;
    if (!suo) return undefined;
    const el = document.querySelector('link[rel="manifest"]');
    const prev = el ? el.getAttribute('href') : null;
    if (el) el.setAttribute('href', suo);
    const titleMeta = document.querySelector('meta[name="apple-mobile-web-app-title"]');
    const prevTitle = titleMeta ? titleMeta.getAttribute('content') : null;
    if (titleMeta) titleMeta.setAttribute('content', 'Verifiche');
    return () => {
      if (el && prev) el.setAttribute('href', prev);
      if (titleMeta && prevTitle) titleMeta.setAttribute('content', prevTitle);
    };
  }, [adapter, token]);
  // App nativa: niente zoom (pinch/doppio-tap) e pagina statica. viewport no-zoom (efficace in
  // standalone) + blocco dei gesti pinch iOS. Ripristina il viewport allo smontaggio.
  useEffect(() => {
    const vp = document.querySelector('meta[name="viewport"]');
    const prev = vp ? vp.getAttribute('content') : null;
    if (vp) vp.setAttribute('content', 'width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no, viewport-fit=cover');
    const stop = (e) => e.preventDefault();
    document.addEventListener('gesturestart', stop);
    document.addEventListener('gesturechange', stop);
    document.addEventListener('gestureend', stop);
    return () => {
      if (vp && prev) vp.setAttribute('content', prev);
      document.removeEventListener('gesturestart', stop);
      document.removeEventListener('gesturechange', stop);
      document.removeEventListener('gestureend', stop);
    };
  }, []);
  const polls = useRef({});
  const focusRef = useRef(null);   // rispecchia `focus`: la callback dell'interval cattura un `focus` stale, il ref no
  useEffect(() => { focusRef.current = focus; }, [focus]);
  const covMeta = useRef({});   // jobId -> { addr, auto } : indirizzo usato e se e' gia' un ritentativo automatico
  const covTimAddr = useRef({});   // jobId covtim -> indirizzo completo (con provincia) per la mappa del PDF
  const lucMeta = useRef({});          // id job luc -> codice fiscale (per screenshot/CF nel risultato live)
  const [lucAperto, setLucAperto] = useState(null);   // dialog consistenze lucchetto
  const [player, setPlayer] = useState(null);          // player registrazione 456 { id, numero }

  useEffect(() => {
    document.body.classList.add('pub-body');
    return () => { document.body.classList.remove('pub-body'); Object.values(polls.current).forEach(clearInterval); };
  }, []);

  const caricaStats = useCallback(() => {
    adapter.loadStats().then(d => setStats(d)).catch(() => {});
    adapter.loadCronologia().then(items => setMieVerifiche(items)).catch(() => {});
  }, [adapter]);
  // riferimento stabile: l'ascoltatore dei messaggi si registra una volta sola e non deve
  // ri-registrarsi a ogni cambio di adapter
  const caricaStatsRef = useRef(null);
  caricaStatsRef.current = caricaStats;

  // Tap sulla notifica di sistema: il service worker apre /agente?apri=<id della verifica>.
  // Tengo da parte l'id e apro la scheda appena la cronologia e' disponibile.
  const apriRichiesto = useRef(null);
  useEffect(() => {
    try {
      const p = new URLSearchParams(window.location.search).get('apri');
      if (p) {
        apriRichiesto.current = p;
        window.history.replaceState({}, '', window.location.pathname);   // via il parametro: no riaperture al refresh
      }
    } catch (e) { /* URL non leggibile: nessun deep link */ }
  }, []);

  // Tap sulla notifica quando la PWA e' GIA' aperta: il service worker non naviga piu'
  // (su iOS navigare significa ricaricare l'app da zero, e sembrava aprirsi "un'altra
  // sessione"), manda un messaggio. Qui si prende l'id e si ricarica la cronologia:
  // la verifica appena conclusa spesso non c'e' ancora nell'elenco.
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return undefined;
    const ascolta = (ev) => {
      const d = ev.data || {};
      if (d.tipo !== 'apri-notifica') return;
      try {
        const id = new URL(d.url, window.location.origin).searchParams.get('apri');
        if (id) apriRichiesto.current = id;
      } catch (e) { /* indirizzo illeggibile: si ricarica e basta */ }
      caricaStatsRef.current && caricaStatsRef.current();
    };
    navigator.serviceWorker.addEventListener('message', ascolta);
    return () => navigator.serviceWorker.removeEventListener('message', ascolta);
  }, []);

  const caricaCaps = useCallback(() => {
    if (adapter.variant === 'link' && !token) { setCaps('invalid'); return; }
    setCaps(null);
    adapter.loadCaps()
      .then(r => {
        setCaps(r);
        caricaStats();
      })
      .catch((e) => {
        const st = e.response?.status;
        // 401 = sessione agente scaduta/non valida -> torna al login (non un errore generico)
        if (st === 401 && adapter.onLogout) { adapter.onLogout(); return; }
        // 403 = link scaduto; ogni altro errore (rete, 500, timeout) NON deve degradare
        // silenziosamente a "solo 456": stato 'error' -> schermata con Riprova.
        setCaps(st === 403 ? 'invalid' : 'error');
      });
  }, [adapter, caricaStats, token]);
  useEffect(() => { caricaCaps(); }, [caricaCaps]);

  // Primo accesso (solo agente): proponi il tutorial una volta.
  useEffect(() => {
    if (adapter.variant !== 'agente' || !caps || caps === 'invalid' || promptedRef.current) return;
    promptedRef.current = true;
    try { if (!localStorage.getItem('agenteTutorialVisto')) setPromptTutorial(true); } catch { /* no-op */ }
  }, [adapter.variant, caps]);

  const topoFetcher = adapter.topo;

  const upJob = (id, patch) => setJobs(js => js.map(j => j.id === id ? { ...j, ...patch } : j));

  const startPoll = (id, tipo) => {
    const t0 = Date.now();
    polls.current[id] = setInterval(async () => {
      try {
        const s = tipo === 'num' ? await adapter.ricercaStato(id) : tipo === 'luc' ? await adapter.lucchettoStato(id) : await adapter.coperturaStato(id);
        const prog = s.stato === 'in_coda' ? 5 : Math.min(94, Math.round((Date.now() - t0) / (tipo === 'num' ? 900 : 700)));
        upJob(id, { stato: s.stato, fase: s.fase, prog, posizione: s.posizione });
        if (s.stato === 'completato' || s.stato === 'errore') {
          clearInterval(polls.current[id]); delete polls.current[id];
          const ris = tipo === 'num' ? s : s.risultato;
          const ok = tipo === 'num' ? (s.risultato && s.risultato !== 'ERRORE')
                   : tipo === 'luc' ? (ris && !s.errore)
                   : (ris && ris.trovato);
          const badCreds = (tipo === 'cov' && s.risultato?.errore === 'bad_credentials');
          const covFail = tipo === 'cov' && !ok;
          const meta = covMeta.current[id]; delete covMeta.current[id];
          const cf = lucMeta.current[id]; delete lucMeta.current[id];
          setFocus(f => f === id ? null : f);
          caricaStats();
          // credenziali SalesMate errate -> ricarica caps (ssmate_ko) per notifica + blocco
          if (badCreds || (tipo === 'luc' && s.risultato?.bad_credentials)) {
            adapter.loadCaps().then(setCaps).catch(() => {});
          }
          // Copertura fallita al 1° tentativo (non credenziali): ritenta UNA volta in automatico,
          // in silenzio, togliendo il job fallito. Se anche il ritentativo fallisce -> resta nella bollicina.
          if (covFail && meta && !meta.auto && !badCreds) {
            setJobs(js => js.filter(j => j.id !== id));
            avviaCopertura(meta.addr, true);
            return;
          }
          const vf = tipo === 'num' ? s.gea_risultato : null;
          const esito = tipo === 'num'
              ? (ok ? `${s.risultato}${vf ? ` · Vodafone: ${vf === 'VODAFONE' ? 'Sì' : 'No'}` : ''}` : 'Verifica non riuscita')
              : tipo === 'luc'
              ? (ok ? `${ris?.codice_fiscale || ris?.numero || ''} · ${ris?.lucchetto || ris?.esito || 'Verificato'}` : 'Verifica non riuscita')
              : (ok ? 'Tocca per l\'esito' : 'Verifica non riuscita');
          upJob(id, { stato: s.stato, prog: 100, risultato: ris, errore: s.errore, nuovo: true, ok, esito, cf });
        }
      } catch { /* ritenta */ }
    }, 1200);
  };

  // Poll dedicato allo strumento Dominio: shape diverso ({stato, dati, step}), non passa da startPoll.
  const startPollDom = (id) => {
    const t0 = Date.now();
    polls.current[id] = setInterval(async () => {
      try {
        const s = await adapter.dominioStato(id);
        const prog = s.stato === 'estrazione' ? 12 : Math.min(94, Math.round((Date.now() - t0) / 1600));
        upJob(id, { stato: s.stato, prog, dati: s.dati, step: s.step });
        if (s.stato === 'completato' || s.stato === 'errore') {
          clearInterval(polls.current[id]); delete polls.current[id];
          setFocus(f => f === id ? null : f);
          caricaStats();
          // durante il dominio le credenziali SalesMate possono risultare KO -> ricarica caps (blocco+notifica)
          adapter.loadCaps().then(setCaps).catch(() => {});
          const ok = s.stato === 'completato';
          const dati = s.dati || {};
          const esito = ok
            ? (dati.ragione_sociale || dati.partita_iva || 'Completato · tocca per l\'esito')
            : (dati.errore || 'Verifica non riuscita');
          upJob(id, { stato: s.stato, prog: 100, dati: s.dati, step: s.step, nuovo: true, ok, esito });
        }
      } catch { /* ritenta */ }
    }, 1500);
  };


  // Poll dedicato allo strumento Indaga: shape {stato, dati}, come il dominio ma senza step.
  const startPollInd = (id) => {
    const t0 = Date.now();
    polls.current[id] = setInterval(async () => {
      try {
        const s = await adapter.indagaStato(id);
        const prog = Math.min(94, Math.round((Date.now() - t0) / 1400));
        upJob(id, { stato: s.stato, prog, dati: s.dati });
        if (s.stato === 'completato' || s.stato === 'errore') {
          clearInterval(polls.current[id]); delete polls.current[id];
          setFocus(f => f === id ? null : f);
          caricaStats();
          const dati = s.dati || {};
          const ok = s.stato === 'completato' && !!dati.ragione_sociale;
          const esito = ok ? dati.ragione_sociale
            : (dati.errore ? 'Ricerca non riuscita' : 'Nessun risultato trovato');
          upJob(id, { stato: s.stato, prog: 100, dati, nuovo: true, ok, esito });
        }
      } catch { /* ritenta */ }
    }, 1400);
  };

  const avviaNumero = async (arg) => {
    const n = ((typeof arg === 'string' ? arg : numero) || '').trim(); if (!n) return;   // arg da 'Vicine'
    try {
      const r = await adapter.ricercaStart(n);
      const job = { id: r.id, tipo: 'num', stato: 'in_coda', prog: 4, min: false };
      setJobs(js => [...js, job]); setFocus(r.id); startPoll(r.id, 'num');
    } catch (e) { alertErr(e); }
  };
  const avviaCopertura = async (addr, auto = false) => {
    if (caps?.ssmate_ko) { setSsmErr(''); setSsmOpen(true); return; }
    const a = (addr && addr.provincia !== undefined) ? addr : cov;
    // Mai uscire in silenzio: un pulsante che non fa niente e non dice niente e' il
    // difetto piu' difficile da capire per chi lo usa (e da cercare per chi lo ripara).
    if (!a || !a.provincia || !a.comune || !(a.indirizzo || '').trim() || !(a.civico || '').trim()) {
      setToasts(ts => [...ts, { id: 'e' + Date.now(), tipo: 'cov', titolo: 'Indirizzo incompleto',
        sub: 'Servono comune, via e civico: riseleziona l’indirizzo.' }]);
      return;
    }
    try {
      const r = await adapter.coperturaStart(a);
      covMeta.current[r.id] = { addr: a, auto: !!auto };
      const job = { id: r.id, tipo: 'cov', stato: 'in_coda', prog: 4, min: false };
      setJobs(js => [...js, job]); setFocus(r.id); startPoll(r.id, 'cov');
    } catch (e) { alertErr(e); }
  };
  // Copertura agente, alternativa a SalesMate: interroga il sito TIM. Gira come JOB SERVER-SIDE
  // 'covtim' (come gli altri strumenti): prosegue a PWA chiusa/sospesa e a fine arriva la notifica push.
  // ── PDF condivisibile dei risultati copertura (stesso layout del link desktop, senza invio email) ──
  const utentePdf = (caps && (caps.nome || caps.username)) || '';
  const makeCovTimPdf = useCallback(
    () => makeCoperturaTimPdf(covTimRes || {}, utentePdf, (covTimRes || {})._addr),
    [covTimRes, utentePdf]);
  const makeCovPdf = useCallback(() => {
    const ris = (aperto && aperto.risultato) || {};
    return makeCoperturaPdf(ris, utentePdf, {
      colonne: (ris.colonne && Object.keys(ris.colonne).length) ? ris.colonne : colonneDaEsito(ris.esito),
      gruppi: RIEP_GRUPPI, label: COL_LABEL, valore: valorePdfCov,
    });
  }, [aperto, utentePdf]);
  // Mostra l'esito della copertura TIM portandosi dietro l'indirizzo cercato (serve alla mappa del PDF).
  const mostraCovTim = (res, addr) => setCovTimRes(addr ? { ...res, _addr: addr } : res);
  const startPollCovTim = (id) => {
    const t0 = Date.now();
    let ko = 0;
    polls.current[id] = setInterval(async () => {
      try {
        const s = await adapter.coperturaTimStato(id);
        ko = 0;
        upJob(id, { prog: Math.min(94, 6 + Math.round((Date.now() - t0) / 320)) });
        if (s.stato === 'completato' || s.stato === 'errore') {
          clearInterval(polls.current[id]); delete polls.current[id];
          const res = s.risultato || { errore: 'Nessun risultato' };
          const err = s.stato === 'errore' || !!res.errore;
          const esito = err ? (res.errore || 'Verifica non riuscita')
            : `${res.esito || '—'}${res.tecnologia ? ' · ' + res.tecnologia : ''}`;
          upJob(id, { stato: s.stato, prog: 100, nuovo: true, ok: !err, esito, risultato: res });
          caricaStats();   // come gli altri poll: aggiorna contatori + cronologia (dove l'esito e' salvato)
          // Se l'utente stava ancora guardando, apri subito l'esito; altrimenti resta nella bollicina.
          // preferisco l'indirizzo locale: ha anche la provincia (il job server non la memorizza)
          if (focusRef.current === id) { setFocus(null); mostraCovTim(res, covTimAddr.current[id] || s.indirizzo); }
          delete covTimAddr.current[id];
        }
      } catch (e) {
        // 404 = job perso (backend riavviato): non tornera' mai -> chiudo, altrimenti resterebbe
        // "in corso" per sempre. Errori di rete: ritento, ma non all'infinito.
        if (e.response?.status === 404 || ++ko > 60) {
          clearInterval(polls.current[id]); delete polls.current[id];
          const res = { errore: 'Verifica non più disponibile' };
          upJob(id, { stato: 'errore', prog: 100, nuovo: true, ok: false, esito: res.errore, risultato: res });
          if (focusRef.current === id) { setFocus(null); setCovTimRes(res); }
        }
      }
    }, 1500);
  };
  const lanciaCoperturaTim = async (ind) => {
    if (!ind || !adapter.coperturaTimStart) return;
    const label = [ind.comune, ind.indirizzo, ind.civico].filter(Boolean).join(' ').trim();
    try {
      const r = await adapter.coperturaTimStart(ind);   // una firma sola: vedi coperturaTimCorpo
      const addr = { comune: ind.comune, via: ind.indirizzo, civico: ind.civico, provincia: ind.provincia };
      covTimAddr.current[r.id] = addr;
      setJobs(js => [...js, { id: r.id, tipo: 'covtim', stato: 'in_corso', prog: 6, min: false, label, addr }]);
      setFocus(r.id); startPollCovTim(r.id);
    } catch (e) { alertErr(e); }
  };
  const avviaLucchetto = async () => {
    if (caps?.ssmate_ko) { setSsmErr(''); setSsmOpen(true); return; }
    const c = cf.trim(); if (!c) return;
    try {
      const r = await adapter.lucchettoStart(c);
      lucMeta.current[r.id] = c;
      const job = { id: r.id, tipo: 'luc', stato: 'in_coda', prog: 4, min: false };
      setJobs(js => [...js, job]); setFocus(r.id); startPoll(r.id, 'luc');
    } catch (e) { alertErr(e); }
  };
  const avviaDominio = async (arg, indirizzoMappa) => {
    if (caps?.ssmate_ko) { setSsmErr(''); setSsmOpen(true); return; }
    const d = ((typeof arg === 'string' ? arg : dominioVal) || '').trim(); if (!d) return;   // arg da 'Vicine'
    try {
      // `indirizzoMappa` arriva solo da Vicine ed e' un ripiego: il motore usa il sito se ce l'ha.
      const r = await adapter.dominioStart(d, indirizzoMappa);
      const job = { id: r.id, tipo: 'dom', stato: 'estrazione', prog: 4, min: false, dominio: d };
      setJobs(js => [...js, job]); setFocus(r.id); startPollDom(r.id);
    } catch (e) { alertErr(e); }
  };
  const avviaIndaga = async (arg) => {
    const n = ((typeof arg === 'string' ? arg : indagaNum) || '').trim(); if (!n) return;
    try {
      const r = await adapter.indagaStart(n);
      const job = { id: r.id, tipo: 'ind', stato: 'cerca', prog: 4, min: false, numero: n };
      setJobs(js => [...js, job]); setFocus(r.id); startPollInd(r.id);
    } catch (e) { alertErr(e); }
  };
  const alertErr = (e) => {
    // il backend fornisce il messaggio giusto: 429 raffica/tetto ("Attendi…"), 403 link non valido/non abilitato
    setToasts(ts => [...ts, { id: 'e' + Date.now(), tipo: 'num', titolo: 'Attenzione',
      sub: e.response?.data?.detail || 'Errore, riprova tra qualche secondo' }]);
  };
  const salvaSsmate = async () => {
    if (!ssmPwd) { setSsmErr('Inserisci la password SalesMate'); return; }
    setSsmBusy(true); setSsmErr('');
    try {
      await adapter.aggiornaSsmate(ssmPwd);
      setSsmOpen(false); setSsmPwd('');
      adapter.loadCaps().then(setCaps).catch(() => {});   // ricarica caps -> ssmate_ko a false, sblocca
      setToasts(ts => [...ts, { id: 'ssm' + Date.now(), tipo: 'num', titolo: 'Credenziali SalesMate aggiornate', sub: 'Copertura e Lucchetto sbloccati' }]);
    } catch (e) {
      setSsmErr(e.response?.data?.detail || 'Errore, riprova');
    } finally { setSsmBusy(false); }
  };

  const chiudiToast = (id) => setToasts(ts => ts.filter(t => t.id !== id));
  const jobFocus = jobs.find(j => j.id === focus && j.stato !== 'completato' && j.stato !== 'errore');
  // Tutte le verifiche non a fuoco (in background + completate) vivono nella bollicina unica.
  const tracked = jobs.filter(j => j.id !== focus);
  const inCorso = (j) => j.stato !== 'completato' && j.stato !== 'errore';
  // Almeno una verifica nella bollicina e' in corso -> anima la linea (cardiogramma).
  const bubbleRunning = tracked.some(inCorso);
  // Verifica dominio in corso: usa 456+Copertura+Lucchetto in un'unica sessione -> blocca le altre tab.
  const domBusy = jobs.some(j => j.tipo === 'dom' && inCorso(j));

  const apriEsito = (j) => {
    if (j.tipo === 'luc') {
      const ris = j.risultato || {}; const m = ris.mapped || {};
      setLucAperto({ lucchetto: ris.lucchetto, codiceFiscale: j.cf,
        ragioneSociale: (m.ssmate_dettaglio || {}).ragione_sociale,
        lavorabile: m.ssmate_lavorabile, dettaglio: m.ssmate_dettaglio, hasShot: !!ris.screenshot });
    } else if (j.tipo === 'dom') {
      setAperto({ id: j.id, tipo: 'dom', dati: j.dati, step: j.step });
    } else if (j.tipo === 'ind') {
      setAperto({ id: j.id, tipo: 'ind', dati: j.dati, numero: j.numero });
    } else {
      setCovTab('RIEPILOGO'); setAperto({ id: j.id, tipo: j.tipo, risultato: j.risultato });
    }
  };

  // Lucchetto nel dettaglio Dominio: apre SOLO le consistenze (niente screenshot). Se il dettaglio
  // non e' nel record (domini vecchi) lo recupera dal server per CF (dai sotto-record lucchetto).
  const apriLucDominio = async (s, dati) => {
    let det = s.dettaglio, luc = s.lucchetto || s.esito, rag = (s.dettaglio || {}).ragione_sociale || dati.ragione_sociale;
    if ((!det || !Object.keys(det).length) && adapter.lucchettoConsistenze && dati.partita_iva) {
      try {
        const r = await adapter.lucchettoConsistenze(dati.partita_iva);
        det = r.dettaglio; luc = luc || r.lucchetto; rag = rag || r.ragione_sociale;
      } catch { /* best-effort */ }
    }
    setLucAperto({ lucchetto: luc, codiceFiscale: dati.partita_iva, ragioneSociale: rag,
      lavorabile: s.lavorabile, dettaglio: det, hasShot: false });
  };

  // ── Cronologia: elenco piatto in ordine cronologico (nessun raggruppamento) ──
  const fmtData = (at) => at ? new Date(at).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '';
  const apribile = (d) => d.tipo === 'dominio' ? true
    : d.tipo === 'indaga' ? !!(d.dati && d.dati.ragione_sociale)
    : d.tipo === 'lucchetto'
    ? ((d.dettaglio && Object.keys(d.dettaglio).length > 0) || d.has_screenshot)
    : d.tipo === 'copertura' ? covStato(d) === 'ok'
    : d.tipo === 'coperturatim' ? !!(d.risultato && !d.risultato.errore)
    : (d.tipo === '456' || !d.tipo) ? !!d.has_registrazione : false;   // 456: occhio se c'e' la registrazione
  const apriDettaglio = (d) => {
    if ((d.tipo === '456' || !d.tipo) && d.has_registrazione) { setPlayer({ id: d.id, numero: d.numero }); return; }
    if (d.tipo === 'dominio') { setAperto({ tipo: 'dom', dati: d.dati, step: d.step }); return; }
    if (d.tipo === 'indaga') { setAperto({ tipo: 'ind', dati: d.dati, numero: d.numero }); return; }
    if (d.tipo === 'coperturatim') { mostraCovTim(d.risultato, d.indirizzo); return; }
    if (d.tipo === 'lucchetto') setLucAperto({ lucchetto: d.lucchetto, codiceFiscale: d.codice_fiscale,
      ragioneSociale: (d.dettaglio || {}).ragione_sociale, lavorabile: undefined, dettaglio: d.dettaglio, hasShot: !!d.has_screenshot });
    else if (d.tipo === 'copertura') { setCovTab('RIEPILOGO'); setAperto({ tipo: 'cov', risultato: { trovato: d.trovato, colonne: d.colonne, indirizzo: d.indirizzo, esito: d.esito } }); }
  };
  // Deep link dalla notifica di sistema (?apri=<id>): apro la scheda appena la cronologia c'e'.
  useEffect(() => {
    const id = apriRichiesto.current;
    if (!id || !mieVerifiche.length) return;
    const d = mieVerifiche.find(x => x.id === id);
    if (!d) return;
    apriRichiesto.current = null;
    if (apribile(d)) { setPanelOpen(false); apriDettaglio(d); }
  }, [mieVerifiche]);   // eslint-disable-line
  // Icone dei 3 passi del dominio: accese (verdi) se fatte, spente (grigie) se non recuperate.
  const DOM_STEP_ICO = { n456: IcoNum, copertura: IcoCov, lucchetto: IcoLuc };
  const esitoNode = (d) => {
    if (d.tipo === 'dominio') { const st = d.step || {};
      return <span style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
        {['n456', 'copertura', 'lucchetto'].map(k => {
          const Ico = DOM_STEP_ICO[k];
          const acceso = st[k] && st[k].stato === 'fatto';
          return <span key={k} title={DOM_STEP_LABEL[k]} style={{ display: 'inline-flex', flex: '0 0 auto',
            color: acceso ? 'var(--ok)' : 'var(--faint)', opacity: acceso ? 1 : 0.45 }}><Ico size={16} /></span>;
        })}
        <EyeIcon />
      </span>; }
    if (d.tipo === 'lucchetto') return <span style={{ color: light ? '#b7791f' : '#ffc95b', display: 'flex', alignItems: 'center', gap: 6 }}>{d.lucchetto || d.esito || '—'}{apribile(d) && <EyeIcon />}</span>;
    if (d.tipo === 'copertura') { const st = covStato(d);
      return st === 'errore' ? <span style={{ color: 'var(--warn)', display: 'flex', alignItems: 'center', gap: 5 }}><WarnIcon />Non riuscita</span>
        : st === 'nontrovato' ? <span style={{ color: 'var(--warn)', display: 'flex', alignItems: 'center', gap: 5 }}><WarnIcon />Non trovato</span>
        : <><span className="tech">{miglioreTecnologia(d)}</span><EyeIcon /></>; }
    if (d.tipo === 'coperturatim') { const r = d.risultato || {};
      const e = (r.esito || '').toUpperCase();
      return r.errore ? <span style={{ color: 'var(--warn)', display: 'flex', alignItems: 'center', gap: 5 }}><WarnIcon />Non riuscita</span>
        : e === 'NON CENSITO' ? <span style={{ color: 'var(--warn)', display: 'flex', alignItems: 'center', gap: 5 }}><WarnIcon />Non trovato<EyeIcon /></span>
        : e === 'NON COPERTO' ? <span style={{ color: 'var(--warn)', display: 'flex', alignItems: 'center', gap: 5 }}><WarnIcon />Non coperto<EyeIcon /></span>
        : <><span className="tech">{r.tecnologia || r.esito || '—'}</span><EyeIcon /></>; }
    if (d.tipo === 'indaga') { const rs = (d.dati || {}).ragione_sociale;
      return rs ? <span style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--ink)', fontWeight: 600 }}>{rs}<EyeIcon /></span>
        : <span style={{ color: 'var(--warn)', display: 'flex', alignItems: 'center', gap: 5 }}><WarnIcon />Nessun risultato</span>; }
    return <>{d.risultato === 'NON TIM' ? <span className="rbadge nt">NON TIM</span> : d.risultato === 'TIM' ? <span className="rbadge tim">TIM</span> : <span>{d.risultato || '—'}</span>}{d.gea_risultato === 'VODAFONE' && <VodafoneBadge size={16} />}{d.has_registrazione && <EyeIcon />}</>;
  };
  const targetLabel = (d) => d.tipo === 'dominio' ? (d.dominio || '—')
    : d.tipo === 'lucchetto' ? (d.codice_fiscale || d.numero || '—')
    : (d.tipo === 'copertura' || d.tipo === 'coperturatim') ? `${(d.indirizzo?.comune || '').toLowerCase()} · ${(d.indirizzo?.via || '').toLowerCase()} ${d.indirizzo?.civico || ''}`
    : d.numero;

  // Cronologia filtrata per lo STRUMENTO attivo (Copertura -> solo copertura, ecc.).
  // La Copertura raccoglie entrambe le fonti: SalesMate ('copertura') e sito TIM ('coperturatim').
  const CRON_TIPO = { num: ['456'], cov: ['copertura', 'coperturatim'], luc: ['lucchetto'], dom: ['dominio'], ind: ['indaga'] };
  const CRON_TITOLO = { num: 'Verifiche 456', cov: 'Copertura', luc: 'Lucchetto', dom: 'Dominio', ind: 'Indaga' };
  const cronFiltro = CRON_TIPO[strum];   // undefined per 'vicine' (ha la sua lista)
  const cronTitolo = CRON_TITOLO[strum] || 'Le tue verifiche';
  // filtro per ESITO (contatore tappato): riduce ulteriormente la lista visibile
  const esitoMatch = (d, f) => {
    if (!f) return true;
    if (f === 'nontim') return d.risultato === 'NON TIM' || d.step?.n456?.esito === 'NON TIM';
    if (f === 'tim') return d.risultato === 'TIM';
    if (f === 'vodafone') return d.gea_risultato === 'VODAFONE';
    // stesso vocabolario del backend: 'errore' non e' ne' coperta ne' scoperta
    if (f === 'coperti') return !!d.esito && !['errore', 'non trovato', 'nessuna copertura'].includes(d.esito);
    if (f === 'scoperti') return ['non trovato', 'nessuna copertura'].includes(d.esito);
    if (f === 'grigio') return d.lucchetto === 'GRIGIO';
    if (f === 'presidiato') return ['NERO', 'ROSSO', 'VIOLA'].includes(d.lucchetto);
    return true;
  };
  const cronVisibili = (cronFiltro ? mieVerifiche.filter(d => cronFiltro.includes(d.tipo || '456')) : mieVerifiche)
    .filter(d => esitoMatch(d, cronEsito));
  // Contatori CONTESTUALI allo strumento (primario grande + secondari, alcuni tappabili per filtrare)
  const contatori = !stats ? null : (
    strum === 'cov' ? [
      { k: stats.copertura, l: 'Verificate' },
      { k: stats.copertura_coperti, l: 'Coperte', good: true, f: 'coperti' },
      { k: stats.copertura_scoperti, l: 'Scoperte', f: 'scoperti' },
    ] : strum === 'luc' ? [
      { k: stats.lucchetto, l: 'Verificati' },
      { k: stats.lucchetto_grigio, l: 'Liberi', good: true, f: 'grigio' },
      { k: stats.lucchetto_presidiato, l: 'Presidiati', f: 'presidiato' },
    ] : strum === 'dom' ? [
      { k: stats.dominio, l: 'Domini' },
      { k: stats.dominio_completi, l: 'Completi', good: true },
      { k: stats.dominio_nontim, l: 'NON TIM', good: true, f: 'nontim' },
    ] : [
      { k: stats.totale_456, l: 'Verifiche' },
      { k: stats.nontim, l: 'NON TIM', good: true, f: 'nontim' },
      { k: stats.tim, l: 'TIM', f: 'tim' },
      ...((caps && caps.vodafone) ? [{ k: stats.vodafone, l: 'Vodafone', f: 'vodafone' }] : []),
    ]
  );

  // ── Tutorial guidato ──
  const avviaTour = () => { setPromptTutorial(false); setMenuOpen(false); setStrum('num'); setTourOpen(true); };

  // ── Notifiche push (banner + badge a fine verifica). Solo PWA agente installata (iOS: da Safari). ──
  useEffect(() => {
    if (adapter.variant === 'agente') statoNotifiche().then(setNotifStato).catch(() => {});
  }, [adapter.variant, menuOpen]);
  // Centro notifiche persistente: carica all'avvio, a intervalli e al ritorno in primo piano.
  const notifAutoRef = useRef(false);   // apre il pannello UNA sola volta, all'apertura dell'app
  const caricaNotifiche = useCallback(() => {
    if (adapter.variant !== 'agente' || !adapter.loadNotifiche) return;
    adapter.loadNotifiche().then(r => {
      setNotifiche(r.items || []); setNonLette(r.non_lette || 0);
      if (!notifAutoRef.current) {
        notifAutoRef.current = true;
        if ((r.items || []).length > 0) setPanelOpen(true);   // ci sono notifiche -> apri il pannello
      }
    }).catch(() => {});
  }, [adapter]);
  useEffect(() => {
    if (adapter.variant !== 'agente') return undefined;
    caricaNotifiche();
    const id = setInterval(caricaNotifiche, 12000);
    const onVis = () => { if (document.visibilityState === 'visible') caricaNotifiche(); };
    document.addEventListener('visibilitychange', onVis);
    return () => { clearInterval(id); document.removeEventListener('visibilitychange', onVis); };
  }, [adapter.variant, caricaNotifiche]);
  // Badge dell'icona = numero di notifiche non lette.
  useEffect(() => { if (adapter.variant === 'agente') setBadge(nonLette); }, [adapter.variant, nonLette]);
  // Apre il dettaglio (se il job e' ancora in sessione). NON scarta: il pallino resta finche' non elimini.
  const apriNotifica = (n) => {
    // Notifica credenziali: scorciatoia diretta alla finestra "cambia password".
    if (n.tipo === 'credenziali') { setSsmErr(''); setSsmPwd(''); setSsmOpen(true); setPanelOpen(false); return; }
    // Copertura sito TIM: l'esito vive nel job server-side -> lo rileggo (funziona anche a PWA riaperta).
    if (n.tipo === 'coperturatim') {
      const jl = jobs.find(x => x.id === n.id && x.risultato)
        || mieVerifiche.find(x => x.id === n.id && x.risultato);   // cronologia: regge anche il riavvio del backend
      if (jl) { mostraCovTim(jl.risultato, jl.addr || jl.indirizzo); setPanelOpen(false); return; }
      if (adapter.coperturaTimStato) {
        adapter.coperturaTimStato(n.id)
          .then(s => { if (s.risultato) { mostraCovTim(s.risultato, s.indirizzo); setPanelOpen(false); } })
          .catch(() => setToasts(ts => [...ts, { id: 'e' + Date.now(), tipo: 'cov',
            titolo: 'Esito non più disponibile', sub: 'Rifai la verifica per questo indirizzo' }]));
      }
      return;
    }
    const j = jobs.find(x => x.id === n.id && x.ok);
    if (j) { apriEsito(j); setPanelOpen(false); return; }
    // Job non piu' in memoria (app riaperta, o verifica finita a PWA chiusa): l'esito e' comunque
    // in cronologia e l'id della notifica e' quello del record -> apro la scheda da li'.
    const d = mieVerifiche.find(x => x.id === n.id);
    if (d && apribile(d)) { apriDettaglio(d); setPanelOpen(false); return; }
    // La cronologia potrebbe non essere ancora arrivata: la ricarico e riprovo una volta sola.
    if (adapter.loadCronologia) {
      adapter.loadCronologia().then(lista => {
        const items = lista || [];
        setMieVerifiche(items);
        const d2 = items.find(x => x.id === n.id);
        if (d2 && apribile(d2)) { apriDettaglio(d2); setPanelOpen(false); }
      }).catch(() => {});
    }
  };
  // Elimina UNA notifica: esce dalla bollicina e il pallino scende.
  const scartaNotifica = (n) => {
    if (adapter.notificaLetta) adapter.notificaLetta(n.id).then(r => setNonLette(r.non_lette || 0)).catch(() => {});
    setNotifiche(prev => prev.filter(x => x.id !== n.id));
  };
  const scartaTutte = () => {
    if (adapter.notificheLetteTutte) adapter.notificheLetteTutte().then(() => setNonLette(0)).catch(() => {});
    setNotifiche([]);
  };

  // ── "Aggiungi ai contatti": da un risultato Dominio/Indaga costruisce un vCard
  // e lo passa al telefono (share sheet -> Contatti su iOS/Android; l'utente conferma). ──
  const contattoDaAperto = (ap) => {
    if (!ap || (ap.tipo !== 'dom' && ap.tipo !== 'ind')) return null;
    const d = ap.dati || {};
    const telefono = d.telefono || d.telefono_maps || '';
    const indirizzo = d.indirizzo || '';
    if (!d.ragione_sociale || !telefono || !indirizzo) return null;   // servono ragione sociale + telefono + indirizzo
    return {
      nome: d.ragione_sociale, telefono, indirizzo,
      comune: d.comune || d.ind_comune || '', provincia: d.provincia || d.ind_provincia || '', cap: d.cap || '',
      sito: d.sito || '', email: d.email || '', piva: d.partita_iva || '', categoria: d.categoria || '',
      logo: d.logo_b64 || '',
    };
  };
  const vcEsc = (s) => String(s || '').replace(/\\/g, '\\\\').replace(/;/g, '\\;').replace(/,/g, '\\,').replace(/\n/g, '\\n');
  // RFC 2426: righe > 75 ottetti piegate, continuazione con uno spazio iniziale (serve per PHOTO base64).
  const vcFold = (line) => {
    if (line.length <= 75) return line;
    let out = line.slice(0, 75), rest = line.slice(75);
    while (rest.length > 74) { out += '\r\n ' + rest.slice(0, 74); rest = rest.slice(74); }
    return out + '\r\n ' + rest;
  };
  const buildVCard = (c) => {
    const L = ['BEGIN:VCARD', 'VERSION:3.0', `N:${vcEsc(c.nome)};;;;`, `FN:${vcEsc(c.nome)}`, `ORG:${vcEsc(c.nome)}`];
    if (c.telefono) L.push(`TEL;TYPE=WORK,VOICE:${vcEsc(c.telefono)}`);
    if (c.indirizzo || c.comune || c.provincia || c.cap)
      L.push(`ADR;TYPE=WORK:;;${vcEsc(c.indirizzo)};${vcEsc(c.comune)};${vcEsc(c.provincia)};${vcEsc(c.cap)};Italia`);
    if (c.email) L.push(`EMAIL;TYPE=WORK:${vcEsc(c.email)}`);
    if (c.sito) L.push(`URL:${vcEsc(c.sito)}`);
    const note = [c.piva && `P.IVA: ${c.piva}`, c.categoria && `Categoria: ${c.categoria}`].filter(Boolean).join('\n');
    if (note) L.push(`NOTE:${vcEsc(note)}`);
    if (c.logo) {
      const b64 = String(c.logo).replace(/^data:[^,]*,/, '');
      if (b64) L.push(vcFold(`PHOTO;ENCODING=b;TYPE=JPEG:${b64}`));
    }
    L.push('END:VCARD');
    return L.join('\r\n');
  };
  const aggiungiContatto = (c) => {
    if (!c) return;
    const vcf = buildVCard(c);
    const fname = `${(c.nome || 'contatto').replace(/[^\w\-]+/g, '_').slice(0, 40) || 'contatto'}.vcf`;
    const url = URL.createObjectURL(new Blob([vcf], { type: 'text/vcard' }));
    const isIOS = /iP(hone|ad|od)/.test(navigator.userAgent)
      || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    if (isIOS) {
      // iOS: il foglio di condivisione NON offre "Aggiungi ai contatti" per i vCard.
      // Aprendo il file, invece, iOS mostra l'anteprima contatto con "Aggiungi contatto".
      window.location.href = url;
    } else {
      // Android/desktop: scarica il .vcf (Android apre l'import nei Contatti).
      const a = document.createElement('a'); a.href = url; a.download = fname;
      document.body.appendChild(a); a.click(); a.remove();
    }
    setTimeout(() => URL.revokeObjectURL(url), 15000);
  };
  const NOTIF_LABEL = {
    attive: 'Notifiche attive', da_attivare: 'Attiva notifiche',
    installa_safari: 'Installa l\'app per le notifiche', negato: 'Notifiche bloccate',
    non_https: 'Notifiche: serve https', non_supportata: 'Notifiche non supportate',
  };
  const toggleNotif = async () => {
    if (notifBusy) return;
    setNotifBusy(true);
    let tok = '';
    try { tok = localStorage.getItem('agenteToken') || ''; } catch { tok = ''; }
    try {
      if (notifStato === 'attive') { setNotifStato(await disattivaPush('/agente/push', tok)); }
      else {
        const r = await attivaPush('/agente/push', tok);
        setNotifStato(r);
        if (r === 'installa_safari') alert('Per le notifiche: apri il menu Condividi di Safari e "Aggiungi a Home", poi riprova da qui.');
        else if (r === 'negato') alert('Permesso negato: attiva le notifiche per questa app dalle Impostazioni del telefono.');
      }
    } catch (e) { alert('Notifiche: ' + (e?.message || 'errore')); }
    finally { setNotifBusy(false); }
  };
  const chiudiTour = () => { setTourOpen(false); try { localStorage.setItem('agenteTutorialVisto', '1'); } catch {} };
  const rifiutaTour = () => { setPromptTutorial(false); try { localStorage.setItem('agenteTutorialVisto', '1'); } catch {} };
  const hasCov = caps && caps.copertura;
  const hasLuc = caps && caps.lucchetto && adapter.lucchettoStart;
  const hasDom = caps && caps.dominio && adapter.dominioStart;   // capability master 'dominio' (admin)
  const hasVic = caps && caps.vicine && adapter.vicineTrova;     // strumento "Vicine" (Google Places)
  const hasInd = caps && caps.indaga && adapter.indagaStart;     // strumento "Indaga" (reverse lookup numero)
  const vic = useVicine(adapter, caps && (caps.username || caps.nome));   // stato "Vicine" (per-agente per il ripristino ultima ricerca)
  // dopo una ricerca Vicine, ricarica i caps così il badge crediti si aggiorna
  useEffect(() => { if (vic.items != null && adapter.loadCaps) adapter.loadCaps().then(setCaps).catch(() => {}); }, [vic.items]); // eslint-disable-line
  // dopo l'apertura dei DETTAGLI di un'azienda (che ora consuma 1 credito), ricarica i caps
  useEffect(() => { if (vic.aperto && vic.aperto.loading === false && adapter.loadCaps) adapter.loadCaps().then(setCaps).catch(() => {}); }, [vic.aperto && vic.aperto.id, vic.aperto && vic.aperto.loading]); // eslint-disable-line
  // Animazione tutorial: fa scorrere il selettore avanti fino in fondo e poi indietro a 'Numero',
  // in modo fluido; al termine chiama `done` per sbloccare 'Avanti'.
  const animaSelettore = (done) => {
    const tabs = ['num', ...(hasCov ? ['cov'] : []), ...(hasLuc ? ['luc'] : []), ...(hasDom ? ['dom'] : []), ...(hasInd ? ['ind'] : []), ...(hasVic ? ['vicine'] : [])];
    if (tabs.length < 2) { if (done) done(); return; }
    const seq = [...tabs.slice(1), ...tabs.slice(0, -1).reverse()]; // avanti fino all'ultimo, poi ritorno a 'num'
    const delay = 900;   // piu' lento => lo scroll smooth glissa in modo fluido (con la mano che scorre)
    setSwipeHint(true);
    setStrum('num');
    seq.forEach((t, k) => setTimeout(() => setStrum(t), (k + 1) * delay));
    setTimeout(() => { setSwipeHint(false); if (done) done(); }, (seq.length + 1) * delay + 300);
  };
  const tourSteps = (caps && caps !== 'invalid') ? [
    { title: 'Benvenuto', body: 'Da qui verifichi numeri, copertura e presidio in pochi tap. Ti mostro come, passo per passo.' },
    ...((hasCov || hasLuc || hasDom || hasVic) ? [{ target: '[data-tour="seg"]', title: 'I tuoi strumenti', body: 'Scorri le icone a destra/sinistra: quella che si aggancia al CENTRO e\' lo strumento attivo. Guarda l\'anteprima.', holdNext: true, onEnter: animaSelettore }] : []),
    { target: '[data-tour="num-input"]', title: 'Verifica numero (456)', body: 'Scrivi qui il numero fisso da controllare.', onEnter: () => setStrum('num') },
    { target: '[data-tour="num-btn"]', title: 'Avvia la verifica', body: 'Poi premi Avvia: ti dice subito se e\' TIM oppure no.' },
    ...(hasCov ? [
      { target: '[data-tour="cov-field"]', title: 'Copertura: un campo alla volta', body: 'La copertura si compila a passi, un campo per volta. Prima il COMUNE: scrivi le prime lettere e scegli dal suggerimento, poi premi "Segue". Compare la VIA: stesso modo, poi "Segue". Infine il CIVICO.', onEnter: () => setStrum('cov') },
      ...(adapter.topo && adapter.topo.reverseGeo ? [
        { target: '[data-tour="cov-geo"]', title: 'Localizzami: scorciatoia', body: 'In alternativa, al passo Comune tocca l\'icona della posizione (evidenziata): Comune e Via si compilano da dove ti trovi e ti resta da inserire solo il Civico.', onEnter: () => setStrum('cov') },
      ] : []),
      { target: '[data-tour="cov-btn"]', title: 'Avvia la copertura', body: 'All\'ultimo passo (Civico) questo pulsante diventa "Avvia": premilo per vedere le tecnologie disponibili a quell\'indirizzo.' },
      { demo: 'cov', title: 'L\'icona a occhio della copertura', body: 'A verifica pronta, accanto all\'esito compare l\'icona a occhio (esempio qui sopra): toccala per il dettaglio con tutte le tecnologie vendibili all\'indirizzo (FWA, fibra, xDSL, ADSL, fibra dedicata), divise nelle tab FWA / Pacchetti / Profili.' },
    ] : []),
    ...(hasLuc ? [
      { target: '[data-tour="luc-input"]', title: 'Lucchetto (presidio)', body: 'Inserisci il codice fiscale del cliente.', onEnter: () => setStrum('luc') },
      { target: '[data-tour="luc-btn"]', title: 'Avvia il presidio', body: 'Premi Avvia per lo stato del lucchetto.' },
      { demo: 'luc', title: 'L\'icona a occhio del presidio', body: 'Sul risultato del lucchetto tocca l\'icona a occhio evidenziata qui sopra: apri questo dettaglio con la ragione sociale del cliente, le consistenze divise per categoria (Azienda, Fisso, Mobile, ICT) e lo screenshot del portale SalesMate.' },
    ] : []),
    ...(hasDom ? [
      { target: '[data-tour="dom-input"]', title: 'Verifica dominio: a cosa serve', body: 'Parti da un sito web e l\'app risale all\'azienda (partita IVA, telefono, indirizzo), poi verifica in un colpo solo 456, copertura e presidio. Comodo quando dell\'azienda hai solo il sito. Scrivi qui il dominio, es. azienda.it.', onEnter: () => setStrum('dom') },
      { target: '[data-tour="dom-btn"]', title: 'Avvia il dominio', body: 'Premi Avvia: l\'app indaga il sito e lancia tutte le verifiche insieme. Avanzamento ed esito arrivano nella bollicina in basso a destra.' },
    ] : []),
    ...(hasVic ? [
      { target: '[data-tour="vic-btn"]', title: 'Aziende vicine: a cosa serve', body: 'Quando sei in giro, trova le aziende (SRL/SPA) intorno a te partendo dalla tua posizione: vedi subito chi c\'e\' vicino da contattare. Scegli il raggio e premi Avvia.', onEnter: () => setStrum('vicine') },
      { title: 'Cosa ottieni dalle vicine', body: 'Ogni azienda mostra se e\' gia\' in archivio (TIM o LEAD) oppure nuova. Toccala per lanciare 456 o Dominio su quell\'attivita\'. In alto filtri per SRL/SPA e in fondo alla lista puoi caricarne altre.' },
    ] : []),
    { target: '[data-tour="cron"]', title: 'Le tue verifiche', body: 'Qui trovi i risultati in ordine cronologico, dal piu\' recente. Le righe con l\'icona a occhio (copertura e lucchetto) si aprono col dettaglio: toccale.', onEnter: () => setStrum('num') },
    { demo: 'notif', title: 'Le notifiche', body: 'Ogni verifica che avvii finisce nella bollicina in basso a destra: quelle in corso mostrano il progresso, quelle finite l\'esito con un pallino "novita\'" finche\' non le apri. Tocca la bollicina per espanderle tutte.' },
    { target: '[data-tour="menu"]', title: 'Menu', body: 'Qui trovi Tema chiaro/scuro, Notifiche, questo Tutorial e le Impostazioni (credenziali SalesMate e posizione della barra strumenti: in alto o in fondo).', onEnter: () => setMenuOpen(false) },
    { title: 'Tutto qui!', body: 'Puoi rivedere il tutorial quando vuoi dal menu, in alto a destra.' },
  ] : [];

  if (caps === null) return <div className="pubdash"><div className="bg" /><div className="wrap">Caricamento…</div></div>;
  if (caps === 'error') return (
    <div className={`pubdash${light ? ' light' : ''}`}><div className="bg" />
      <div className="wrap" style={{ textAlign: 'center', paddingTop: '18vh' }}>
        <div className="card" style={{ maxWidth: 380, margin: '0 auto' }}>
          <h2 style={{ margin: '0 0 6px', fontSize: 18 }}>Servizi non raggiungibili</h2>
          <p style={{ color: 'var(--muted)', fontSize: 14, margin: '0 0 14px' }}>
            Non è stato possibile caricare i tuoi servizi. Controlla la connessione e riprova.
          </p>
          <button className="btn" onClick={caricaCaps}>Riprova</button>
        </div>
      </div>
    </div>
  );
  if (adapter.variant === 'link' && caps === 'invalid') return (
    <div className={`pubdash${light ? ' light' : ''}`}><div className="bg" />
      <div className="invalid"><div className="card">
        <h2 style={{ margin: '0 0 6px', fontSize: 18 }}>Link non valido o scaduto</h2>
        <p style={{ color: 'var(--muted)', fontSize: 14, margin: 0 }}>Contatta il referente per un nuovo link.</p>
      </div></div>
    </div>
  );

  const toolWheelEl = (caps.copertura || (caps.lucchetto && adapter.lucchettoStart) || hasDom || hasVic || hasInd) ? (
    <TabWheel value={strum} onChange={setStrum} lockedTo={domBusy ? 'dom' : null} hint={swipeHint} haptic={vibra}
      tabs={[
        { key: 'num', label: 'Numero', Ico: IcoNum },
        ...(caps.copertura ? [{ key: 'cov', label: 'Copertura', Ico: IcoCov }] : []),
        ...(caps.lucchetto && adapter.lucchettoStart ? [{ key: 'luc', label: 'Lucchetto', Ico: IcoLuc }] : []),
        ...(hasDom ? [{ key: 'dom', label: 'Dominio', Ico: IcoDom, busy: domBusy, dot: domBusy }] : []),
        ...(hasInd ? [{ key: 'ind', label: 'Indaga', Ico: IcoInd }] : []),
        ...(hasVic ? [{ key: 'vicine', label: 'Vicine', Ico: IcoVic }] : []),
      ]} />
  ) : null;

  return (
    <div className={`pubdash${light ? ' light' : ''}${menuPos === 'giu' ? ' menu-giu' : ''}`}>
      <div className="bg" />
      <div className="wrap">
        <header>
          {adapter.variant === 'agente'
            ? <img src="/verifiche-192.png" alt="" style={{ width: 42, height: 42, borderRadius: 12, display: 'block', flexShrink: 0 }} />
            : <div className="logo"><svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="#dfe8ff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2 4 6v6c0 5 3.4 7.5 8 10 4.6-2.5 8-5 8-10V6z" /><path d="m9 12 2 2 4-4" /></svg></div>}
          <div><h1>Centro Verifiche</h1><div className="hsub">{caps.nome || 'accesso collaboratore'}</div></div>
          <div className="hgroup">
            {caps.tetto != null && <div className="pill-user"><span className="dot" /> {caps.usi_oggi}/{caps.tetto} oggi</div>}
            {adapter.onLogout ? (
              <div className="menuw">
                <button data-tour="menu" className="tgl" onClick={() => setMenuOpen(o => !o)} title="Menu" aria-label="Menu" style={{ position: 'relative' }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M3 6h18M3 12h18M3 18h18" /></svg>
                  {caps && caps.ssmate_ko && <span className="notif-dot" />}
                </button>
                {menuOpen && (
                  <>
                    <div className="menu-scrim" onClick={() => setMenuOpen(false)} />
                    <div className="menu">
                      <button onClick={toggleTheme}>
                        {light
                          ? <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M21 12.8A9 9 0 1 1 11.2 3 7 7 0 0 0 21 12.8z" /></svg>
                          : <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="4" /><path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" /></svg>}
                        <span>Tema {light ? 'scuro' : 'chiaro'}</span>
                      </button>
                      {notifStato && (
                        <button onClick={toggleNotif} disabled={notifBusy || notifStato === 'non_https' || notifStato === 'non_supportata'} style={notifStato === 'attive' ? { color: 'var(--ok)' } : undefined}>
                          {notifStato === 'attive'
                            ? <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.7 21a2 2 0 0 1-3.4 0" /></svg>
                            : <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.7 21a2 2 0 0 1-3.4 0" /><line x1="2" y1="2" x2="22" y2="22" /></svg>}
                          <span>{notifBusy ? 'Attendere…' : (NOTIF_LABEL[notifStato] || 'Attiva notifiche')}</span>
                        </button>
                      )}
                      <button onClick={avviaTour}>
                        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><polygon points="10 8 16 12 10 16 10 8" fill="currentColor" stroke="none" /></svg>
                        <span>Tutorial</span>
                      </button>
                      <button onClick={() => { setMenuOpen(false); setImpOpen(true); }}>
                        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="4" y1="8" x2="20" y2="8" /><line x1="4" y1="16" x2="20" y2="16" /><circle cx="9" cy="8" r="2" fill="var(--surface)" /><circle cx="15" cy="16" r="2" fill="var(--surface)" /></svg>
                        <span>Impostazioni</span>
                        {caps && caps.ssmate_ko && <span className="notif-dot" style={{ position: 'static', marginLeft: 'auto' }} />}
                      </button>
                      <button onClick={() => { setMenuOpen(false); adapter.onLogout(); }}>
                        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><polyline points="16 17 21 12 16 7" /><line x1="21" y1="12" x2="9" y2="12" /></svg>
                        <span>Esci</span>
                      </button>
                      <div style={{ padding: '9px 12px 5px', marginTop: 4, borderTop: '1px solid var(--line)', fontSize: 11, color: 'var(--faint)', letterSpacing: '.02em' }}>
                        Versione {APP_VERSION}
                      </div>
                    </div>
                  </>
                )}
              </div>
            ) : (
              <button className="tgl" onClick={toggleTheme} title="Chiaro/scuro">
                {light ? <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M21 12.8A9 9 0 1 1 11.2 3 7 7 0 0 0 21 12.8z" /></svg>
                       : <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="4" /><path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" /></svg>}
              </button>
            )}
          </div>
        </header>

        {caps.ssmate_ko && (
          <button
            onClick={() => { setSsmErr(''); setSsmPwd(''); setSsmOpen(true); }}
            style={{
              display: 'flex', alignItems: 'center', gap: 10, width: '100%',
              margin: '2px 0 12px', padding: '11px 14px', borderRadius: 12,
              border: '1px solid var(--err)', background: 'var(--surface-2)',
              color: 'var(--err)', font: 'inherit', fontSize: 13, fontWeight: 600,
              textAlign: 'left', cursor: 'pointer',
            }}>
            <span className="notif-dot" style={{ position: 'static', flexShrink: 0 }} />
            <span style={{ flex: 1, lineHeight: 1.35 }}>
              Credenziali SalesMate non valide: la password è cambiata. Tocca qui per aggiornarla e sbloccare Copertura e Lucchetto.
            </span>
            <span style={{
              flexShrink: 0, padding: '5px 11px', borderRadius: 999,
              background: 'var(--err)', color: '#fff', fontSize: 12, fontWeight: 700,
            }}>Aggiorna ›</span>
          </button>
        )}

        <div className="dash">
          <div className="col-tools">
            {menuPos === 'su' && toolWheelEl}
            <div className={`tool t-num pane${strum === 'num' ? ' on' : ''}`}>
            <div className="top"><div className="ic"><IcoNum size={21} /></div>
              <div><h3>Verifica numero</h3><div className="sub">{caps.vodafone ? '456 + Vodafone automatico' : '456'}</div></div></div>
            <div className="field">
              <input data-tour="num-input" className="in" placeholder="Es. 0461 123456" value={numero} onChange={e => setNumero(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') avviaNumero(); }} />
              <button data-tour="num-btn" className="btn" onClick={avviaNumero}>Avvia</button>
            </div>
            {caps.vodafone && <div className="foot">Se NON TIM o TIM non attiva parte anche Vodafone</div>}
          </div>

          {caps.copertura && (
            <div className={`tool t-cov pane${strum === 'cov' ? ' on' : ''}`}>
              <div className="top"><div className="ic"><IcoCov size={21} /></div>
                <div><h3>Copertura SSMATE</h3><div className="sub">FWA · Fibra · xDSL per indirizzo</div></div></div>
              <div className="field col" data-tour="cov-field">
                <AddressCascade variant="public" fetcher={topoFetcher} stepper rowLayout inputClass="in"
                  submitLabel="Avvia" submitDisabled={adapter.variant === 'agente' ? false : caps.ssmate_ko}
                  submitDataTour="cov-btn" btnClass="btn"
                  onSubmit={() => {
                    // La fonte si propone solo a chi ha il permesso di usarla: vedi fonteCopertura
                    const f = fonteCopertura(adapter.variant, caps);
                    if (f === 'scelta') setCovScelta(cov);
                    else if (f === 'tim') lanciaCoperturaTim(cov);
                    else avviaCopertura();
                  }}
                  onChange={(a) => setCov({ provincia: a.provincia, comune: a.comune, indirizzo: a.via, civico: a.civico })} />
              </div>
              {caps.ssmate_ko
                ? (adapter.variant === 'agente'
                    ? <div className="foot" style={{ color: 'var(--warn, #d9822b)' }}>Copertura SalesMate da sbloccare (menu) · la Copertura sito TIM resta disponibile</div>
                    : <div className="foot" style={{ color: 'var(--err)' }}>Credenziali SalesMate da aggiornare (menu) per sbloccare</div>)
                : <div className="foot">Scrivi l'indirizzo col toponimo (es. VIA BRUNO GALAS)</div>}
            </div>
          )}

          {caps.lucchetto && adapter.lucchettoStart && (
            <div className={`tool t-luc pane${strum === 'luc' ? ' on' : ''}`}>
              <div className="top"><div className="ic"><IcoLuc size={21} /></div>
                <div><h3>Lucchetto SSMATE</h3><div className="sub">Verifica presidio commerciale per codice fiscale</div></div>
              </div>
              <div className="field">
                <input data-tour="luc-input" className="in" placeholder="Codice Fiscale" value={cf} onChange={e => setCf(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') avviaLucchetto(); }} />
                <button data-tour="luc-btn" className="btn" onClick={avviaLucchetto} disabled={caps.ssmate_ko}>Avvia</button>
              </div>
              {caps.ssmate_ko && <div className="foot" style={{ color: 'var(--err)' }}>Credenziali SalesMate da aggiornare (menu) per sbloccare</div>}
            </div>
          )}

          {hasDom && (
            <div className={`tool t-dom pane${strum === 'dom' ? ' on' : ''}`}>
              <div className="top"><div className="ic"><IcoDom size={21} /></div>
                <div><h3>Verifica dominio</h3><div className="sub">P.IVA, indirizzo e telefono → 456 · Copertura · Lucchetto</div></div>
              </div>
              <div className="field">
                <input data-tour="dom-input" className="in" placeholder="es. azienda.it" value={dominioVal}
                  disabled={domBusy}
                  onChange={e => setDominioVal(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') avviaDominio(); }} />
                <button data-tour="dom-btn" className="btn" onClick={avviaDominio} disabled={caps.ssmate_ko || domBusy}>
                  {domBusy ? 'In corso…' : 'Avvia'}
                </button>
              </div>
              {caps.ssmate_ko
                ? <div className="foot" style={{ color: 'var(--err)' }}>Credenziali SalesMate da aggiornare (menu) per sbloccare</div>
                : <div className="foot">L'avanzamento e l'esito compaiono nella bollicina in basso a destra</div>}
            </div>
          )}

          {hasInd && (
            <div className={`tool t-ind pane${strum === 'ind' ? ' on' : ''}`}>
              <div className="top"><div className="ic"><IcoInd size={21} /></div>
                <div><h3>Indaga numero</h3><div className="sub">Da un numero risali all'azienda (nome, sito, indirizzo)</div></div>
              </div>
              <div className="field">
                <input className="in" placeholder="Es. 06 12345678" value={indagaNum}
                  onChange={e => setIndagaNum(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') avviaIndaga(); }} />
                <button className="btn" onClick={() => avviaIndaga()}>Indaga</button>
              </div>
              <div className="foot">Ricerca su Google Maps · l'esito compare nella bollicina in basso a destra</div>
            </div>
          )}

          {hasVic && (
            <div className={`tool t-vicine pane${strum === 'vicine' ? ' on' : ''}`}>
              <div className="top"><div className="ic"><IcoVic size={21} /></div>
                <div style={{ flex: 1, minWidth: 0 }}><h3>Aziende vicine</h3><div className="sub">Attività intorno a te → 456 · Dominio</div></div>
              </div>
              <VicineControls vic={vic} crediti={caps.vicine_crediti} />
            </div>
          )}

            {strum === 'vicine' && hasVic ? (
              <VicineFiltri vic={vic} />
            ) : contatori && (
              <div className="statbar">
                {contatori.map((c, i) => (
                  <button key={i} type="button"
                    className={`schip${c.good ? ' good' : ''}${c.f ? ' tap' : ''}${(c.f && cronEsito === c.f) ? ' on' : ''}`}
                    onClick={c.f ? () => setCronEsito(cronEsito === c.f ? null : c.f) : undefined}
                    style={c.f ? undefined : { cursor: 'default' }}>
                    <div className="k">{(c.k ?? 0).toLocaleString('it-IT')}</div><div className="l">{c.l}</div>
                  </button>
                ))}
                <div className="schip oggi"><div className="k">{stats.oggi}</div><div className="l">Oggi</div></div>
              </div>
            )}
          </div>

          <div className="col-cron">
            <div className="cron" data-tour="cron">
              {strum === 'vicine' && hasVic ? (
                <>
                  <div className="ch"><h3>Aziende vicine</h3>{vic.items && <span className="cnt">{vic.items.length} trovate</span>}</div>
                  <div className="clist"><VicineLista vic={vic} onVerifica456={avviaNumero} onDominio={avviaDominio} /></div>
                </>
              ) : (
              <>
              <div className="ch"><h3>{cronTitolo}</h3><span className="cnt">{cronVisibili.length}</span></div>
              <div className="clist">
                {cronVisibili.length === 0 ? (
                  <div style={{ padding: '30px 16px', textAlign: 'center', color: 'var(--muted)', fontSize: 13 }}>Nessuna verifica {cronFiltro ? 'di questo tipo' : ''} ancora. Avviane una: comparirà qui.</div>
                ) : cronVisibili.map((d, i) => {
                  const canOpen = apribile(d);
                  const isCovTim = d.tipo === 'coperturatim';
                  const isLuc = d.tipo === 'lucchetto', isCov = d.tipo === 'copertura' || isCovTim, isDom = d.tipo === 'dominio', isInd = d.tipo === 'indaga';
                  return (
                    <div key={d.id || i} className={`crow${canOpen ? ' ck' : ''}`}
                      onClick={canOpen ? () => apriDettaglio(d) : undefined}>
                      <div className={`cr-ic ${isLuc ? 'luc' : isCov ? 'cov' : isDom ? 'dom' : isInd ? 'ind' : 'num'}`}>{isLuc ? <IcoLuc /> : isCov ? <IcoCov /> : isDom ? <IcoDom /> : isInd ? <IcoInd /> : <IcoNum />}</div>
                      <div className="cr-mid">
                        <div className={`t${isCov ? ' cap' : ''}`}>{targetLabel(d)}</div>
                        <div className="s">{isLuc ? 'Lucchetto' : isCovTim ? 'Copertura · sito TIM' : isCov ? 'Copertura' : isDom ? 'Dominio' : isInd ? 'Indaga' : '456'}{d.at ? ` · ${fmtData(d.at)}` : ''}</div>
                      </div>
                      <div className="cr-res">{esitoNode(d)}</div>
                    </div>
                  );
                })}
              </div>
              </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Overlay progresso */}
      {jobFocus && (
        <div className="scrim">
          <div className={`modal ${AGN[jobFocus.tipo].cls}`}>
            <div className="stage">
              <div className="ring" /><div className="ring spin" /><div className="ring spin2" />
              {(jobFocus.tipo === 'cov' || jobFocus.tipo === 'covtim') && <div className="radar" />}
              <div className="core">{jobFocus.tipo === 'num'
                ? <div className="wave"><i style={{ animationDelay: '0s' }} /><i style={{ animationDelay: '.15s' }} /><i style={{ animationDelay: '.3s' }} /><i style={{ animationDelay: '.15s' }} /><i style={{ animationDelay: '0s' }} /></div>
                : jobFocus.tipo === 'luc'
                  ? <IcoLuc size={24} />
                  : jobFocus.tipo === 'dom'
                    ? <IcoDom size={24} />
                    : jobFocus.tipo === 'ind'
                      ? <IcoInd size={24} />
                      : <IcoCov size={24} />}</div>
            </div>
            <h4>{AGN[jobFocus.tipo].title}</h4>
            <div className="phase">{jobFocus.tipo === 'num'
              ? (jobFocus.stato === 'in_coda' ? 'In coda…' : (AGN.num.phases[jobFocus.fase] || 'Verifica in corso…'))
              : jobFocus.tipo === 'luc'
                ? (jobFocus.stato === 'in_coda' ? 'In coda…' : 'Verifica presidio in corso…')
                : jobFocus.tipo === 'dom'
                  ? (jobFocus.stato === 'estrazione' ? 'Estrazione dati azienda…' : 'Verifiche 456 · Copertura · Lucchetto…')
                  : jobFocus.tipo === 'ind'
                    ? 'Ricerca su Google Maps…'
                    : jobFocus.tipo === 'covtim'
                      ? 'Interrogo il sito TIM…'
                      : (jobFocus.stato === 'in_coda' ? `In coda…${jobFocus.posizione ? ` (${jobFocus.posizione}° in fila)` : ''}` : 'Analisi copertura in corso…')}</div>
            <div className="bar"><i style={{ width: (jobFocus.prog || 4) + '%' }} /></div>
            <div className="actions">
              <button className="ghost" onClick={() => setFocus(null)}>Metti in background</button>
              <button className="ghost" onClick={() => {
                clearInterval(polls.current[jobFocus.id]); delete polls.current[jobFocus.id];
                // Copertura sito TIM: avviso il server, altrimenti a fine verifica arriverebbe la push.
                if (jobFocus.tipo === 'covtim' && adapter.coperturaTimAnnulla) adapter.coperturaTimAnnulla(jobFocus.id).catch(() => {});
                setJobs(js => js.filter(j => j.id !== jobFocus.id)); setFocus(null);
              }}>Annulla</button>
            </div>
          </div>
        </div>
      )}

      {/* Bollicina: verifiche in corso + centro notifiche persistente (contatore = non lette) */}
      {(tracked.some(inCorso) || notifiche.length > 0) && (
        <>
          <button className={`jbubble${nonLette > 0 ? ' nuovo' : ''}`}
            onClick={() => setPanelOpen(o => !o)} aria-label="Notifiche">
            <svg width="23" height="23" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round" className={bubbleRunning ? 'jekg-on' : undefined}>
              <path className="jekg-base" d="M22 12h-4l-3 9L9 3l-3 9H2" />
              <path className="jekg-trace" d="M22 12h-4l-3 9L9 3l-3 9H2" />
            </svg>
            {nonLette > 0 && <span className="jcount">{nonLette}</span>}
          </button>
          {panelOpen && (
            <div className="jpanel">
              <div className="jpanel-h">
                <span>Notifiche</span>
                {notifiche.length > 0 && <button className="jx" style={{ width: 'auto', padding: '0 8px', fontSize: 11 }} onClick={scartaTutte}>Cancella tutte</button>}
                <button className="jx" onClick={() => setPanelOpen(false)}>×</button>
              </div>
              <div className="jpanel-list">
                {tracked.filter(inCorso).map(j => (
                  <div key={j.id} className={`jrow ${j.tipo}`}>
                    <div className="jrow-ic"><span className="jspin" /></div>
                    <div className="jrow-txt" onClick={() => { setFocus(j.id); setPanelOpen(false); }}>
                      <div className="jrow-t">{AGN[j.tipo].title}</div>
                      <div className="jrow-s">{`${j.prog || 0}% · in corso`}</div>
                    </div>
                  </div>
                ))}
                {notifiche.length === 0 && !tracked.some(inCorso) && (
                  <div style={{ padding: '18px 14px', textAlign: 'center', color: 'var(--muted)', fontSize: 12.5 }}>Nessuna notifica</div>
                )}
                {notifiche.map(n => {
                  const isCovN = n.tipo === 'copertura' || n.tipo === 'coperturatim';
                  const Ico = isCovN ? IcoCov : n.tipo === 'lucchetto' ? IcoLuc : n.tipo === 'dominio' ? IcoDom : IcoNum;
                  const cls = isCovN ? 'cov' : n.tipo === 'lucchetto' ? 'luc' : n.tipo === 'dominio' ? 'dom' : 'num';
                  return (
                    <div key={n.id} className={`jrow ${cls}`}>
                      <div className="jrow-ic"><Ico size={15} /></div>
                      <div className="jrow-txt" onClick={() => apriNotifica(n)}>
                        <div className="jrow-t">{n.title}<span style={{ display: 'inline-block', width: 7, height: 7, borderRadius: '50%', background: 'var(--cvf)', marginLeft: 6, verticalAlign: 'middle' }} /></div>
                        <div className="jrow-s">{n.body}{n.at ? ` · ${fmtData(n.at)}` : ''}</div>
                      </div>
                      <button className="jrow-x" onClick={() => scartaNotifica(n)} aria-label="Elimina">×</button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}

      {/* Avvisi brevi (input/stato: errori form, credenziali aggiornate) */}
      {toasts.length > 0 && (
        <div className="toasts">
          {toasts.map(t => (
            <div key={t.id} className={`toast ${t.tipo}`}>
              <div className="ti"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5" /></svg></div>
              <div><h5>{t.titolo}</h5><p>{t.sub}</p></div>
              <div className="x" onClick={() => chiudiToast(t.id)}>×</div>
            </div>
          ))}
        </div>
      )}

      {player && (
        <RegistrazionePlayer src={adapter.registrazioneUrl && adapter.registrazioneUrl(player.id)}
          numero={player.numero} onClose={() => setPlayer(null)} />
      )}

      {lucAperto && (
        <LucchettoConsistenze
          lucchetto={lucAperto.lucchetto}
          codiceFiscale={lucAperto.codiceFiscale}
          ragioneSociale={lucAperto.ragioneSociale}
          lavorabile={lucAperto.lavorabile}
          dettaglio={lucAperto.dettaglio}
          screenshotUrl={lucAperto.hasShot && adapter.lucchettoScreenshotUrl ? adapter.lucchettoScreenshotUrl(lucAperto.codiceFiscale) : null}
          dark={!light}
          onClose={() => setLucAperto(null)}
        />
      )}

      {menuPos === 'giu' && toolWheelEl && <div className="toolbar-bottom">{toolWheelEl}</div>}

      {impOpen && (
        <div className="scrim" onClick={() => setImpOpen(false)}>
          <div className="modal" style={{ textAlign: 'left', width: 'min(400px,94vw)' }} onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 12px', fontSize: 17 }}>Impostazioni</h3>
            {adapter.aggiornaSsmate && (
              <button className="imp-row" onClick={() => { setImpOpen(false); setSsmErr(''); setSsmPwd(''); setSsmOpen(true); }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="7.5" cy="15.5" r="5.5" /><path d="M11.4 11.6 21 2" /><path d="m15.5 7.5 3 3L22 7l-3-3" /></svg>
                <span>Credenziali SalesMate</span>
                {caps && caps.ssmate_ko && <span className="notif-dot" style={{ position: 'static', marginLeft: 'auto' }} />}
              </button>
            )}
            <div className="imp-sec">
              <div className="imp-lbl">Posizione barra strumenti</div>
              <div className="imp-seg">
                <button className={menuPos === 'su' ? 'on' : ''} onClick={() => setMenuPos('su')}>Menu su</button>
                <button className={menuPos === 'giu' ? 'on' : ''} onClick={() => setMenuPos('giu')}>Menu giù</button>
              </div>
            </div>
            {typeof navigator !== 'undefined' && navigator.vibrate && (
              <div className="imp-sec" style={{ marginTop: 14 }}>
                <div className="imp-lbl">Vibrazione allo scorrimento</div>
                <div className="imp-seg">
                  <button className={vibra ? 'on' : ''} onClick={() => { setVibra(true); try { navigator.vibrate(12); } catch { /* no-op */ } }}>Attiva</button>
                  <button className={!vibra ? 'on' : ''} onClick={() => setVibra(false)}>Disattiva</button>
                </div>
              </div>
            )}
            <button className="btn" style={{ width: '100%', marginTop: 16, background: 'var(--surface-2)', color: 'var(--ink)' }} onClick={() => setImpOpen(false)}>Chiudi</button>
          </div>
        </div>
      )}

      {ssmOpen && (
        <div className="scrim" onClick={() => !ssmBusy && setSsmOpen(false)}>
          <div className="modal" style={{ textAlign: 'left', width: 'min(400px,94vw)' }} onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 4px', fontSize: 17 }}>Credenziali SalesMate</h3>
            {caps && caps.ssmate_ko
              ? <p style={{ margin: '0 0 14px', color: 'var(--err)', fontSize: 12.5 }}>Le credenziali SalesMate non sono piu' valide: Copertura e Lucchetto sono bloccati. Reinserisci la password per sbloccarli.</p>
              : <p style={{ margin: '0 0 14px', color: 'var(--muted)', fontSize: 12.5 }}>La tua password SalesMate: e' l'unica password, vale per l'accesso all'app e per Copertura/Lucchetto.</p>}
            <input className="in" value={(caps && (caps.username || caps.nome)) || ''} disabled style={{ marginBottom: 10, opacity: .7 }} />
            <input className="in" type="password" placeholder="Password SalesMate" value={ssmPwd} onChange={e => setSsmPwd(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') salvaSsmate(); }} style={{ marginBottom: 12 }} />
            {ssmErr && <div style={{ color: 'var(--err)', fontSize: 13, marginBottom: 10 }}>{ssmErr}</div>}
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn" style={{ flex: 1, background: 'var(--surface-2)', color: 'var(--ink)' }} disabled={ssmBusy} onClick={() => setSsmOpen(false)}>Annulla</button>
              <button className="btn" style={{ flex: 1, background: 'linear-gradient(180deg,var(--c456),var(--c456b))', color: 'var(--btn-ink)' }} disabled={ssmBusy} onClick={salvaSsmate}>{ssmBusy ? 'Verifico…' : 'Valida e salva'}</button>
            </div>
          </div>
        </div>
      )}

      {promptTutorial && (
        <div className="scrim" onClick={rifiutaTour}>
          <div className="modal" style={{ textAlign: 'center', width: 'min(380px,94vw)' }} onClick={e => e.stopPropagation()}>
            <div style={{ width: 46, height: 46, margin: '0 auto 10px', borderRadius: 14, display: 'grid', placeItems: 'center', background: 'var(--surface-2)', color: 'var(--c456)' }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><polygon points="10 8 16 12 10 16 10 8" fill="currentColor" stroke="none" /></svg>
            </div>
            <h3 style={{ margin: '4px 0 6px', fontSize: 18 }}>Vuoi vedere il tutorial?</h3>
            <p style={{ margin: '0 0 18px', color: 'var(--muted)', fontSize: 13.5 }}>Ti mostro in 30 secondi come verificare numeri, copertura e presidio.</p>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn" style={{ flex: 1, background: 'var(--surface-2)', color: 'var(--ink)' }} onClick={rifiutaTour}>No grazie</button>
              <button className="btn" style={{ flex: 1, background: 'linear-gradient(180deg,var(--c456),var(--c456b))', color: 'var(--btn-ink)' }} onClick={avviaTour}>Sì, mostralo</button>
            </div>
          </div>
        </div>
      )}

      {tourOpen && tourSteps.length > 0 && <TourGuidato steps={tourSteps} onClose={chiudiTour} />}

      {/* Dettaglio esito */}
      {aperto && (
        <div className="scrim" onClick={() => setAperto(null)}>
          <div className={`modal ${aperto.tipo}`} onClick={e => e.stopPropagation()} style={{ textAlign: 'left' }}>
            {aperto.tipo === 'num' ? (
              <div style={{ textAlign: 'center' }}>
                <span className="badge" style={{ background: ESITO_COL[aperto.risultato?.risultato] || 'var(--muted)' }}>{aperto.risultato?.risultato || '—'}</span>
                <div style={{ marginTop: 12, fontSize: 13, color: 'var(--muted)' }}>Numero: <b style={{ color: 'var(--ink)' }}>{aperto.risultato?.numero}</b></div>
                {aperto.risultato?.gea_risultato && <div style={{ marginTop: 6, fontSize: 13, color: 'var(--muted)' }}>Vodafone: <b style={{ color: 'var(--ink)' }}>{aperto.risultato.gea_risultato === 'VODAFONE' ? 'Sì' : 'No'}</b></div>}
              </div>
            ) : aperto.tipo === 'dom' ? (() => {
              const dati = aperto.dati || {};
              const step = aperto.step || {};
              return (
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                    {dati.logo_b64 && <img src={dati.logo_b64} alt="" style={{ width: 44, height: 44, borderRadius: 10, objectFit: 'cover', border: '1px solid var(--line-2)', flexShrink: 0 }} />}
                    <div style={{ fontWeight: 700, color: 'var(--ink)' }}>Verifica dominio</div>
                  </div>
                  <div style={{ fontSize: 13, display: 'grid', gap: 4, marginBottom: 12, color: 'var(--muted)' }}>
                    <div><b style={{ color: 'var(--ink)' }}>Ragione sociale:</b> {dati.ragione_sociale || '—'}</div>
                    <div><b style={{ color: 'var(--ink)' }}>P.IVA:</b> {dati.partita_iva || '—'}</div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <b style={{ color: 'var(--ink)' }}>Telefono:</b> {dati.telefono || '—'}
                      {dati.telefono && <span style={{ fontSize: 11, fontWeight: 800, padding: '2px 8px', borderRadius: 999, color: '#fff', background: dati.telefono_fisso ? 'var(--ok)' : 'var(--warn)' }}>
                        {dati.telefono_tipo || (dati.telefono_fisso ? 'fisso' : 'mobile')}</span>}
                    </div>
                    <div><b style={{ color: 'var(--ink)' }}>Indirizzo:</b> {dati.indirizzo || '—'}</div>
                    {/* La via e' stata scelta per SOMIGLIANZA fra piu' vie dal nome simile:
                        la copertura potrebbe riguardare una strada diversa da quella
                        dell'azienda, e chi legge deve poterlo valutare. */}
                    {dati.ind_via && dati.ind_esatto === false && (
                      <div style={{ marginTop: 6, padding: '7px 9px', borderRadius: 8, fontSize: 12,
                        background: 'color-mix(in srgb, var(--warn) 14%, transparent)',
                        border: '1px solid color-mix(in srgb, var(--warn) 40%, transparent)' }}>
                        <b>Indirizzo simile, non identico.</b> Verificato su <b>{dati.ind_via} {dati.ind_civico}</b>
                        {(dati.ind_alternative || []).length > 0 && <> · in zona esistono anche {(dati.ind_alternative || []).join(', ')}</>}
                      </div>
                    )}
                  </div>
                  <div style={{ border: '1px solid var(--line-2)', borderRadius: 10, overflow: 'hidden' }}>
                    {['n456', 'copertura', 'lucchetto'].map((k, i) => {
                      const s = step[k];
                      // Copertura riuscita e con indirizzo trovato: mostra la migliore tecnologia + occhio
                      // che apre il dettaglio a tab, esattamente come le coperture singole.
                      const covApri = k === 'copertura' && s && s.stato === 'fatto' && s.trovato && (s.colonne || s.esito);
                      // Lucchetto con un colore reale (GRIGIO/VERDE/VIOLA...): occhio -> consistenze + screenshot.
                      // NB: sui domini vecchi il dettaglio non e' salvato -> si apre lo screenshot (per CF).
                      const lucColore = s && s.stato === 'fatto' && s.esito
                        && String(s.esito).toUpperCase() !== 'NON TROVATO';
                      const lucApri = k === 'lucchetto' && lucColore;
                      return (
                        <div key={k} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 12px', borderTop: i ? '1px solid var(--line-2)' : 'none' }}>
                          <span style={{ fontWeight: 700, color: 'var(--ink)' }}>{DOM_STEP_LABEL[k]}</span>
                          {covApri ? (
                            <span style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}
                              onClick={() => { setCovTab('RIEPILOGO'); setAperto({ tipo: 'cov', risultato: { trovato: s.trovato, colonne: s.colonne, indirizzo: s.indirizzo, esito: s.esito } }); }}>
                              <span className="tech">{miglioreTecnologia({ colonne: s.colonne, esito: s.esito })}</span><EyeIcon />
                            </span>
                          ) : lucApri ? (
                            <span style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}
                              onClick={() => apriLucDominio(s, dati)}>
                              <span style={{ fontWeight: 700 }}>{s.lucchetto || s.esito}</span><EyeIcon />
                            </span>
                          ) : statoStepDom(s)}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })() : aperto.tipo === 'ind' ? (() => {
              const d = aperto.dati || {};
              const soc = d.social || {};
              const socList = Object.keys(soc).filter(k => soc[k]);
              const Riga = ({ l, children }) => (children ? (
                <div style={{ display: 'flex', gap: 8 }}>
                  <b style={{ color: 'var(--ink)', minWidth: 78, flexShrink: 0 }}>{l}:</b>
                  <span style={{ wordBreak: 'break-word' }}>{children}</span>
                </div>
              ) : null);
              return (
                <div>
                  <div style={{ fontWeight: 700, color: 'var(--ink)', marginBottom: 2 }}>Indaga numero</div>
                  <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 10 }}>Numero: <b style={{ color: 'var(--ink)' }}>{aperto.numero || d.numero || '—'}</b></div>
                  {!d.ragione_sociale ? (
                    <div style={{ fontSize: 13, color: 'var(--warn)' }}>
                      {d.errore ? 'Ricerca non riuscita. Riprova tra poco.' : 'Nessuna azienda trovata su Google Maps per questo numero.'}
                    </div>
                  ) : (
                    <div style={{ fontSize: 13, display: 'grid', gap: 6, color: 'var(--muted)' }}>
                      <Riga l="Azienda">{d.ragione_sociale}</Riga>
                      <Riga l="Categoria">{d.categoria}</Riga>
                      <Riga l="Indirizzo">{d.indirizzo}</Riga>
                      <Riga l="Città">{[d.comune, d.provincia && `(${d.provincia})`].filter(Boolean).join(' ')}</Riga>
                      <Riga l="Telefono">{d.telefono_maps}</Riga>
                      <Riga l="P.IVA">{d.partita_iva}</Riga>
                      <Riga l="Sito">{d.sito && <a href={d.sito} target="_blank" rel="noreferrer" style={{ color: 'var(--accent)' }}>{d.sito.replace(/^https?:\/\//, '').replace(/\/$/, '')}</a>}</Riga>
                      <Riga l="Email">{d.email}</Riga>
                      {socList.length > 0 && (
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                          <b style={{ color: 'var(--ink)', minWidth: 78, flexShrink: 0 }}>Social:</b>
                          {socList.map(k => <a key={k} href={soc[k]} target="_blank" rel="noreferrer" style={{ color: 'var(--accent)', textTransform: 'capitalize' }}>{k}</a>)}
                        </div>
                      )}
                      {d.maps_url && <div style={{ marginTop: 4 }}><a href={d.maps_url} target="_blank" rel="noreferrer" style={{ color: 'var(--accent)', fontSize: 12 }}>Apri su Google Maps ↗</a></div>}
                    </div>
                  )}
                </div>
              );
            })() : (() => {
              const ris = aperto.risultato || {};
              const colonne = ris.colonne || colonneDaEsito(ris.esito);
              const tab = TABS.find(t => t.id === covTab) || TABS[0];
              const isRiep = covTab === 'RIEPILOGO';
              const keys = tab.keys || COLS.filter(c => (colonne[c.k] || {}).esito === 'OK').map(c => c.k);
              const groups = isRiep
                ? RIEP_GRUPPI.map(t => ({ id: t.id, keys: t.keys.filter(k => (colonne[k] || {}).esito === 'OK') })).filter(g => g.keys.length)
                : [{ id: tab.id, keys }];
              const totale = groups.reduce((n, g) => n + g.keys.length, 0);
              return (
                <div>
                  <div style={{ fontWeight: 700, color: 'var(--ink)', textTransform: 'capitalize', marginBottom: 2 }}>
                    {(ris.indirizzo?.comune || '').toLowerCase()} · {(ris.indirizzo?.via || '').toLowerCase()} {ris.indirizzo?.civico || ''}
                  </div>
                  {(() => {
                    const st = covStato(ris);
                    const msg = st === 'errore' ? 'Verifica non riuscita. Riprova.'
                      : st === 'nontrovato' ? 'Indirizzo non trovato su SSMATE'
                      : 'Indirizzo trovato · Copertura SSMATE';
                    return <div style={{ fontSize: 12, color: st === 'ok' ? 'var(--ok)' : (st === 'errore' ? 'var(--err)' : 'var(--warn)'), marginBottom: 10 }}>{msg}</div>;
                  })()}
                  {covStato(ris) !== 'ok' ? null : (
                    <>
                      <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
                        {TABS.map(t => (
                          <button key={t.id} onClick={() => setCovTab(t.id)} style={{
                            flex: 1, padding: '8px 6px', fontSize: 12, fontWeight: 700, cursor: 'pointer', borderRadius: 10,
                            border: '1px solid ' + (covTab === t.id ? 'var(--accent-line)' : 'var(--line-2)'),
                            background: covTab === t.id ? 'var(--accent-soft)' : 'var(--surface-2)',
                            color: covTab === t.id ? 'var(--accent)' : 'var(--muted)',
                          }}>{t.id}</button>
                        ))}
                      </div>
                      <div style={{ minHeight: Math.max(260, covH) }}>
                        <div ref={covContentRef}>
                          {totale === 0 ? (
                            <div style={{ fontSize: 13, color: 'var(--muted)' }}>{covTab === 'RIEPILOGO' ? 'Nessuna tecnologia vendibile su questo indirizzo.' : 'Nessun dato in questa sezione.'}</div>
                          ) : groups.map((g, gi) => (
                            <div key={g.id}>
                              {isRiep && <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '.6px', textTransform: 'uppercase', color: 'var(--accent)', background: 'var(--accent-soft)', border: '1px solid var(--accent-line)', borderRadius: 8, padding: '5px 9px', margin: gi ? '12px 0 6px' : '0 0 6px' }}>{g.id}</div>}
                              {g.keys.map(k => { const c = COLS.find(x => x.k === k); return (
                                <div key={k} className="rowline"><span className="l">{c.l}</span><span>{cell(c, colonne[k])}</span></div>
                              ); })}
                            </div>
                          ))}
                          {!ris.colonne && covTab !== 'RIEPILOGO' && <div style={{ fontSize: 11, color: 'var(--faint)', marginTop: 8 }}>Riepilogo da verifica precedente: dettaglio velocità/fascia non disponibile.</div>}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              );
            })()}
            <div className="actions">
              {/* Il sito e' il dato da cui nasce tutta la verifica dominio: poterlo aprire
                  da qui evita di ricopiare l'indirizzo a mano quando serve un'occhiata.
                  Si apre fuori dall'app (nuova scheda), senza portarsi dietro la sessione. */}
              {(aperto.tipo === 'dom' || aperto.tipo === 'ind') && (aperto.dati || {}).sito && (
                <button className="ghost" onClick={() => {
                  const u = (aperto.dati || {}).sito;
                  try { window.open(/^https?:\/\//i.test(u) ? u : `https://${u}`, '_blank', 'noopener,noreferrer'); }
                  catch (e) { /* apertura bloccata: nessun danno */ }
                }}>Apri il sito</button>
              )}
              {contattoDaAperto(aperto) && (
                <button className="ghost" onClick={() => aggiungiContatto(contattoDaAperto(aperto))}>Aggiungi ai contatti</button>
              )}
              {aperto.tipo === 'cov' && covStato(aperto.risultato || {}) === 'ok' && (
                <CondividiPdfBtn make={makeCovPdf} />
              )}
              <button className="ghost" onClick={() => setAperto(null)}>Chiudi</button>
            </div>
          </div>
        </div>
      )}

      {/* Copertura agente: scelta SalesMate/sito TIM, dopo aver premuto Avvia */}
      {covScelta && (
        <div className="scrim" onClick={() => setCovScelta(null)}>
          <div className="modal cov" style={{ width: 'min(380px,94vw)' }} onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 6px', fontSize: 17 }}>Copertura: come verifico?</h3>
            <p style={{ margin: '0 0 16px', color: 'var(--muted)', fontSize: 13 }}>Scegli la fonte per questo indirizzo.</p>
            <div className="actions" style={{ flexDirection: 'column' }}>
              {/* L'indirizzo va passato ESPLICITAMENTE: premendo Avvia il modulo si azzera
                  subito (per essere pronto a un nuovo indirizzo), quindi quando si sceglie
                  qui lo stato e' gia' vuoto e la verifica non partiva, in silenzio. */}
              <button className="btn" style={{ width: '100%' }}
                onClick={() => { const ind = covScelta; setCovScelta(null); avviaCopertura(ind); }}>Copertura SalesMate</button>
              <button className="btn" style={{ width: '100%', background: 'var(--surface-2)', color: 'var(--ink)' }}
                onClick={() => { const ind = covScelta; setCovScelta(null); lanciaCoperturaTim(ind); }}>Copertura sito TIM</button>
            </div>
          </div>
        </div>
      )}

      {/* Copertura agente: esito della verifica sul sito TIM */}
      {covTimRes && (
        <div className="scrim" onClick={() => setCovTimRes(null)}>
          <div className="modal cov" style={{ width: 'min(420px,94vw)', textAlign: 'left' }} onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 12px', fontSize: 17, textAlign: 'center' }}>Copertura sito TIM</h3>
            {covTimRes?.errore ? (
              <div style={{ color: 'var(--err)', fontSize: 13.5, textAlign: 'center' }}>{covTimRes.errore}</div>
            ) : (
              <>
                <div style={{ textAlign: 'center', marginBottom: 12 }}>
                  <span className="badge" style={{ background: esitoColorTim(covTimRes.esito), fontSize: 14, padding: '6px 14px' }}>
                    {covTimRes.esito || '—'}{covTimRes.tecnologia ? ` · ${covTimRes.tecnologia}` : ''}
                  </span>
                </div>
                {covTimRes.raw && <div className="rowline"><span className="l">Verdetto</span><span>{covTimRes.raw}</span></div>}
                <div className="rowline"><span className="l">Tecnologia</span><span>{covTimRes.tecnologia || '—'}</span></div>
                <div className="rowline"><span className="l">Download</span><span>{fmtVelTim(covTimRes.down) || '—'}</span></div>
                <div className="rowline"><span className="l">Upload</span><span>{fmtVelTim(covTimRes.up) || '—'}</span></div>
                <div className="rowline"><span className="l">Latenza</span><span>{covTimRes.latenza != null && covTimRes.latenza !== '' ? `${covTimRes.latenza} ms` : '—'}</span></div>
                {covTimRes.indirizzo_norm && <div className="rowline"><span className="l">Indirizzo</span><span>{covTimRes.indirizzo_norm}</span></div>}
                {covTimRes.tecnologia === 'FWA' && covTimRes.down == null && covTimRes.up == null && (
                  <div style={{ fontSize: 11.5, color: 'var(--faint)', marginTop: 8 }}>Per l'FWA il sito TIM non pubblica le velocità (mostra solo la latenza).</div>
                )}
              </>
            )}
            <div className="actions">
              {/* PDF solo per un esito utile: su NON CENSITO (indirizzo non trovato) non c'e' nulla da condividere */}
              {!covTimRes.errore && (covTimRes.esito || '').toUpperCase() !== 'NON CENSITO' && (
                <CondividiPdfBtn make={makeCovTimPdf} titolo="Copertura sito TIM" />
              )}
              <button className="ghost" onClick={() => setCovTimRes(null)}>Chiudi</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
